import "bootstrap/dist/css/bootstrap.min.css";
import {
  AlertCircle,
  Calendar,
  CheckCircle,
  Clock,
  DollarSign,
  Edit2,
  Package,
  Plus,
  Target,
  Trash2,
  User,
  XCircle,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import BotaoRelatorio from "../components/BotaoRelatorio";

const API_BASE_URL = process.env.REACT_APP_API_URL;

// ⚙️ Filtros avançados do modal de movimentos (botão "Filtros", painel de
// fornecedor/período/valor e os chips). Trocar para true religa.
// Proposital não reaproveitar a flag de documentos: são recursos diferentes e
// não faz sentido um arrastar o outro.
const FILTROS_HABILITADOS = false;


// ✅ BUSCA DO MODAL "LISTAGEM DE MOVIMENTOS"

// Remove acentos e caixa para a comparação ser tolerante ("ACAO" acha "Ação")
const normalizarTexto = (valor) =>
  String(valor ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ") // NBSP do "R$ 1,00" vira espaço comum
    .toLowerCase()
    .trim();

const moedaParaTexto = (valor) =>
  Number(valor ?? 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });

// Pill do totalizador: fica vermelha quando a soma é negativa (estornos)
const classeTotalMovimento = (modificador, valor) =>
  `totais-movimentos__item totais-movimentos__item--${modificador}` +
  (valor < 0 ? " totais-movimentos__item--negativo" : "");

// Um valor é indexado em três formas, porque cada pessoa digita de um jeito:
// número cru ("16163.43"), formatado ("R$ 16.163,43") e sem separador de
// milhar ("R$ 16163,43").
const textoBuscavelValor = (valor) => {
  const numero = Number(valor ?? 0);
  const formatado = moedaParaTexto(numero);
  return `${numero} ${formatado} ${formatado.replace(/\./g, "")}`;
};

// Texto pesquisável de cada coluna.
const camposFiltraveisMovimento = (mov) => ({
  idMov: String(mov.idMov ?? ""),
  data: String(mov.data ?? ""),
  fornecedor: [
    mov.fornecedor,
    mov.origem !== "N/A" ? mov.origem : "",
    mov.destino !== "N/A" ? mov.destino : "",
  ].join(" "),
  valor: textoBuscavelValor(mov.valor),
  empenhado: textoBuscavelValor(mov.empenhado),
  status: String(mov.status ?? ""),
  tipo: String(mov.tipo ?? ""),
});

// ✅ ORDENAÇÃO DO MODAL "LISTAGEM DE MOVIMENTOS"
// Padrão: data crescente (mais antiga primeiro), igual ao ORDER BY da consulta
const ORDENACAO_MOV_PADRAO = { coluna: "data", direcao: "asc" };

// `data` já chega formatada como "dd/mm/aaaa", que não ordena como texto.
// Usamos a data crua (dataOrig) e, se faltar, remontamos a partir do exibido.
const dataMovimentoParaNumero = (mov) => {
  if (mov.dataOrig) {
    const tempo = new Date(mov.dataOrig).getTime();
    if (!Number.isNaN(tempo)) return tempo;
  }
  const partes = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(String(mov.data ?? ""));
  if (partes) {
    return new Date(`${partes[3]}-${partes[2]}-${partes[1]}T00:00:00`).getTime();
  }
  return 0; // "N/A" e datas inválidas vão para o começo na ordem crescente
};

const VALOR_ORDENACAO_MOVIMENTO = {
  idMov: (mov) => Number(mov.idMov) || 0,
  data: dataMovimentoParaNumero,
  fornecedor: (mov) => normalizarTexto(mov.fornecedor),
  valor: (mov) => Number(mov.valor) || 0,
  empenhado: (mov) => Number(mov.empenhado) || 0,
  status: (mov) => normalizarTexto(mov.status),
  tipo: (mov) => normalizarTexto(mov.tipo),
};

const compararMovimentos = (a, b, coluna) => {
  const extrair = VALOR_ORDENACAO_MOVIMENTO[coluna];
  if (!extrair) return 0;

  const valorA = extrair(a);
  const valorB = extrair(b);

  if (typeof valorA === "number" && typeof valorB === "number") {
    return valorA - valorB;
  }
  return String(valorA).localeCompare(String(valorB), "pt-BR");
};

// ✅ FILTROS AVANÇADOS DO MODAL "LISTAGEM DE MOVIMENTOS"
// Complementam a busca livre (que é texto solto em qualquer coluna) com o que
// texto não resolve: fornecedor exato, intervalo de data e de valor. Ficam num
// painel que abre sob demanda — a faixa fixa de inputs por coluna já foi
// tentada e pesou demais no visual (ver HISTORICO de 03/07).
const FILTROS_MOV_VAZIO = {
  fornecedor: "",
  dataDe: "",
  dataAte: "",
  valorMin: "",
  valorMax: "",
  somenteComValor: false,
  somenteComEmpenhado: false,
};

// Coluna que cada filtro destaca no cabeçalho da tabela
const COLUNA_DO_FILTRO = {
  fornecedor: "fornecedor",
  dataDe: "data",
  dataAte: "data",
  valorMin: "valor",
  valorMax: "valor",
  somenteComValor: "valor",
  somenteComEmpenhado: "empenhado",
};

// Aceita "1234,56", "1.234,56", "R$ 1.234,56" e "1234.56" — cada um digita de
// um jeito. Campo vazio (ou impossível de ler) vira null, ou seja: sem limite.
const numeroDoFiltro = (texto) => {
  const limpo = String(texto ?? "")
    .replace(/[R$\s]/gi, "")
    .trim();
  if (!limpo) return null;
  // o ponto só é separador de milhar quando fecha um grupo de 3 dígitos:
  // "1.234,56" perde o ponto, "1234.56" mantém
  const numero = Number(
    limpo.replace(/\.(?=\d{3}(\D|$))/g, "").replace(",", ".")
  );
  return Number.isNaN(numero) ? null : numero;
};

// "aaaa-mm-dd" do <input type="date"> vira timestamp. O limite superior pega o
// fim do dia, senão "até 03/02" jogaria fora os movimentos do próprio dia 03/02.
const dataDoFiltro = (texto, fimDoDia = false) => {
  if (!texto) return null;
  const tempo = new Date(
    `${texto}T${fimDoDia ? "23:59:59.999" : "00:00:00"}`
  ).getTime();
  return Number.isNaN(tempo) ? null : tempo;
};

const dataFiltroParaTexto = (iso) => {
  const [ano, mes, dia] = String(iso).split("-");
  return dia ? `${dia}/${mes}/${ano}` : iso;
};

// Um movimento passa quando atende a TODOS os filtros preenchidos (E entre si,
// e depois E com a busca livre).
const movimentoPassaNosFiltros = (mov, filtros) => {
  if (filtros.fornecedor && mov.fornecedor !== filtros.fornecedor) return false;

  const valor = Number(mov.valor) || 0;
  const empenhado = Number(mov.empenhado) || 0;

  // "somente com" olha != 0, não > 0: estorno é negativo, mas tem valor
  if (filtros.somenteComValor && valor === 0) return false;
  if (filtros.somenteComEmpenhado && empenhado === 0) return false;

  const minimo = numeroDoFiltro(filtros.valorMin);
  const maximo = numeroDoFiltro(filtros.valorMax);
  if (minimo !== null && valor < minimo) return false;
  if (maximo !== null && valor > maximo) return false;

  const de = dataDoFiltro(filtros.dataDe);
  const ate = dataDoFiltro(filtros.dataAte, true);
  if (de !== null || ate !== null) {
    // mesma função da ordenação: usa dataOrig e cai no "dd/mm/aaaa" exibido
    const data = dataMovimentoParaNumero(mov);
    // sem data legível (N/A) o movimento fica fora de qualquer período
    if (!data) return false;
    if (de !== null && data < de) return false;
    if (ate !== null && data > ate) return false;
  }

  return true;
};

// Vira os chips acima da tabela — cada um removível pela sua chave
const descreverFiltrosAtivos = (filtros) => {
  const chips = [];
  const minimo = numeroDoFiltro(filtros.valorMin);
  const maximo = numeroDoFiltro(filtros.valorMax);

  if (filtros.fornecedor) {
    chips.push({
      chave: "fornecedor",
      rotulo: `Fornecedor: ${filtros.fornecedor}`,
    });
  }
  if (filtros.dataDe) {
    chips.push({
      chave: "dataDe",
      rotulo: `A partir de ${dataFiltroParaTexto(filtros.dataDe)}`,
    });
  }
  if (filtros.dataAte) {
    chips.push({
      chave: "dataAte",
      rotulo: `Até ${dataFiltroParaTexto(filtros.dataAte)}`,
    });
  }
  if (minimo !== null) {
    chips.push({ chave: "valorMin", rotulo: `Valor ≥ ${moedaParaTexto(minimo)}` });
  }
  if (maximo !== null) {
    chips.push({ chave: "valorMax", rotulo: `Valor ≤ ${moedaParaTexto(maximo)}` });
  }
  if (filtros.somenteComValor) {
    chips.push({ chave: "somenteComValor", rotulo: "Somente com valor líquido" });
  }
  if (filtros.somenteComEmpenhado) {
    chips.push({ chave: "somenteComEmpenhado", rotulo: "Somente com empenhado" });
  }

  return chips;
};

export default function BudgetDashboard() {
  // ✅ TODOS OS ESTADOS PRIMEIRO (na ordem correta)
  const [data, setData] = useState([]);
  const [dataTotal, setDataTotal] = useState([]);

  // Estados para os movimentos
  const [movimentosData, setMovimentosData] = useState([]);
  const [loadingMovimentos, setLoadingMovimentos] = useState(false);

  // ✅ ESTADO DA BUSCA E DA ORDENAÇÃO DO MODAL DE MOVIMENTOS
  const [buscaMovimentos, setBuscaMovimentos] = useState("");
  const [ordenacaoMovimentos, setOrdenacaoMovimentos] =
    useState(ORDENACAO_MOV_PADRAO);
  const [colunaHoverMovimentos, setColunaHoverMovimentos] = useState(null);
  const [filtrosMovimentos, setFiltrosMovimentos] = useState(FILTROS_MOV_VAZIO);
  const [filtrosAbertos, setFiltrosAbertos] = useState(false);

  const buscaMovimentosAtiva = buscaMovimentos.trim() !== "";

  // Chips dos filtros avançados ativos; a contagem vira o badge do botão
  const filtrosAtivos = useMemo(
    () => descreverFiltrosAtivos(filtrosMovimentos),
    [filtrosMovimentos]
  );
  const temFiltroAvancado = filtrosAtivos.length > 0;
  const movimentosEstaoFiltrados = buscaMovimentosAtiva || temFiltroAvancado;

  // Fornecedores da lista já carregada, com quantos movimentos cada um tem —
  // vira o select, que evita digitar o nome e errar acento/grafia
  const fornecedoresMovimentos = useMemo(() => {
    const contagem = new Map();
    movimentosData.forEach((mov) => {
      const nome = mov.fornecedor || "N/A";
      contagem.set(nome, (contagem.get(nome) || 0) + 1);
    });
    return [...contagem.entries()]
      .map(([nome, quantidade]) => ({ nome, quantidade }))
      .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
  }, [movimentosData]);

  const movimentosFiltrados = useMemo(() => {
    const busca = normalizarTexto(buscaMovimentos);

    // filtros avançados primeiro (comparação de número, mais barata) e a
    // busca livre por cima do que sobrou
    const porFiltro = temFiltroAvancado
      ? movimentosData.filter((movimento) =>
          movimentoPassaNosFiltros(movimento, filtrosMovimentos)
        )
      : movimentosData;

    const filtrados = !busca
      ? porFiltro
      : porFiltro.filter((movimento) =>
          Object.values(camposFiltraveisMovimento(movimento)).some((campo) =>
            normalizarTexto(campo).includes(busca)
          )
        );

    const { coluna, direcao } = ordenacaoMovimentos;
    if (!coluna) return filtrados;

    const fator = direcao === "asc" ? 1 : -1;
    // cópia: sort() altera o array e não podemos mexer em movimentosData
    return [...filtrados].sort(
      (a, b) => compararMovimentos(a, b, coluna) * fator
    );
  }, [
    movimentosData,
    buscaMovimentos,
    ordenacaoMovimentos,
    filtrosMovimentos,
    temFiltroAvancado,
  ]);

  // Colunas em que a busca casou — são essas que ficam destacadas, para o
  // usuário enxergar de onde veio o resultado
  const colunasComResultado = useMemo(() => {
    const colunas = new Set();
    const busca = normalizarTexto(buscaMovimentos);
    if (!busca) return colunas;

    movimentosFiltrados.forEach((movimento) => {
      Object.entries(camposFiltraveisMovimento(movimento)).forEach(
        ([coluna, campo]) => {
          if (normalizarTexto(campo).includes(busca)) colunas.add(coluna);
        }
      );
    });
    return colunas;
  }, [movimentosFiltrados, buscaMovimentos]);

  // Colunas com filtro avançado ativo — destacadas junto com as da busca
  const colunasComFiltro = useMemo(() => {
    const colunas = new Set();
    filtrosAtivos.forEach(({ chave }) => {
      const coluna = COLUNA_DO_FILTRO[chave];
      if (coluna) colunas.add(coluna);
    });
    return colunas;
  }, [filtrosAtivos]);

  // Totalizador do rodapé — soma sempre o que está visível (lista já filtrada)
  const totaisMovimentos = useMemo(
    () =>
      movimentosFiltrados.reduce(
        (acc, mov) => {
          const realizado = Number(mov.valor) || 0;
          const empenhado = Number(mov.empenhado) || 0;
          return {
            realizado: acc.realizado + realizado,
            empenhado: acc.empenhado + empenhado,
            total: acc.total + realizado + empenhado,
          };
        },
        { realizado: 0, empenhado: 0, total: 0 }
      ),
    [movimentosFiltrados]
  );

  // Abrir/fechar o modal volta busca, filtros e ordenação ao padrão
  const resetarModalMovimentos = () => {
    setBuscaMovimentos("");
    setOrdenacaoMovimentos(ORDENACAO_MOV_PADRAO);
    setColunaHoverMovimentos(null);
    setFiltrosMovimentos(FILTROS_MOV_VAZIO);
    setFiltrosAbertos(false);
  };

  const alterarFiltroMovimentos = (chave, valor) =>
    setFiltrosMovimentos((anterior) => ({ ...anterior, [chave]: valor }));

  // O ✕ do chip devolve aquela chave ao valor vazio dela ("" ou false)
  const removerFiltroMovimentos = (chave) =>
    alterarFiltroMovimentos(chave, FILTROS_MOV_VAZIO[chave]);

  const limparFiltrosMovimentos = () => setFiltrosMovimentos(FILTROS_MOV_VAZIO);

  // 1º clique na coluna ordena (crescente), clique seguinte inverte
  const alternarOrdenacaoMovimentos = (coluna) => {
    setOrdenacaoMovimentos((anterior) =>
      anterior.coluna === coluna
        ? { coluna, direcao: anterior.direcao === "asc" ? "desc" : "asc" }
        : { coluna, direcao: "asc" }
    );
  };

  // Cabeçalho clicável: ordena e fica destacado quando a busca casou nele.
  // A seta aparece só na coluna ordenada (ou apagada na que está sob o mouse).
  const renderThMovimento = (coluna, rotulo, minWidth) => {
    const buscaCasou = colunasComResultado.has(coluna);
    const filtrada = colunasComFiltro.has(coluna);
    const destacada = buscaCasou || filtrada;
    const ordenado = ordenacaoMovimentos.coluna === coluna;
    const crescente = ordenacaoMovimentos.direcao === "asc";
    const sobMouse = colunaHoverMovimentos === coluna;

    const dicas = [];
    if (buscaCasou) dicas.push(`A busca encontrou resultados em ${rotulo}`);
    if (filtrada) dicas.push(`Filtro ativo em ${rotulo}`);
    dicas.push(
      ordenado
        ? `Ordenado ${crescente ? "crescente" : "decrescente"} — clique para inverter`
        : `Clique para ordenar por ${rotulo}`
    );

    return (
      <th
        role="button"
        onClick={() => alternarOrdenacaoMovimentos(coluna)}
        onMouseEnter={() => setColunaHoverMovimentos(coluna)}
        onMouseLeave={() => setColunaHoverMovimentos(null)}
        style={{
          minWidth,
          cursor: "pointer",
          userSelect: "none",
          whiteSpace: "nowrap",
          ...(destacada
            ? {
                backgroundColor: "#ffc107",
                color: "#212529",
                boxShadow: "none",
              }
            : {}),
        }}
        title={dicas.join(" | ")}
      >
        {destacada && "🔎 "}
        {rotulo}
        {/* span sempre presente: reserva o espaço e evita a coluna "pular" */}
        <span
          className="ms-1"
          style={{
            opacity: ordenado ? 1 : sobMouse ? 0.55 : 0,
            transition: "opacity .12s",
          }}
        >
          {ordenado ? (crescente ? "▲" : "▼") : "⇅"}
        </span>
      </th>
    );
  };


  // Estados para os itens do movimento
  const [itensMovimento, setItensMovimento] = useState([]);
  const [loadingItens, setLoadingItens] = useState(false);

  // Estados para loading
  const [salvandoObservacao, setSalvandoObservacao] = useState(false);
  const [salvandoAlerta, setSalvandoAlerta] = useState(false);
  const [loadingItensInvestimento, setLoadingItensInvestimento] =
    useState(false);

  // Estados para o sistema de versões
  const [modalVersaoVisible, setModalVersaoVisible] = useState(null);
  const [versoesData, setVersoesData] = useState([]);
  const [loadingVersoes, setLoadingVersoes] = useState(false);
  const [versaoDetalhada, setVersaoDetalhada] = useState(null);

  // Estados para o modal de investimento
  const [salvandoItens, setSalvandoItens] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const PpCODCOLIGADA = localStorage.getItem("PCODCOLIGADA");
  const PpCODFILIAL = localStorage.getItem("PCODFILIAL");
  const PpANO = localStorage.getItem("selectedYear");
  const PpTIPO = "Investimento";
  const PpCODUSUARIO = localStorage.getItem("username");

  // ✅ NOVOS ESTADOS PARA O MODAL DE LOADING DOS RELATÓRIOS
  const [relatorioLoading, setRelatorioLoading] = useState(false);
  const [relatorioTipo, setRelatorioTipo] = useState("");
  const [relatorioNome, setRelatorioNome] = useState("");
  const [relatorioProgresso, setRelatorioProgresso] = useState(0);
  const [relatorioMensagem, setRelatorioMensagem] = useState("");
  const [relatorioResultado, setRelatorioResultado] = useState(null);
  const [relatorioInterval, setRelatorioInterval] = useState(null);

  // ✅ FUNÇÃO PARA INICIAR GERAÇÃO DO RELATÓRIO
  const iniciarGeracaoRelatorio = useCallback(
    (tipo, nome) => {
      // ✅ MENSAGENS MOTIVACIONAIS PARA O LOADING
      const mensagensMotivacionais = [
        "📊 Compilando dados financeiros...",
        "🔍 Analisando movimentações orçamentárias...",
        "📈 Processando informações gerenciais...",
        "💰 Consolidando valores por centro de custo...",
        "📋 Formatando relatório para visualização...",
        "🎯 Preparando insights para tomada de decisão...",
        "⚡ Otimizando dados para melhor performance...",
        "🔄 Sincronizando informações mais recentes...",
      ];

      setRelatorioLoading(true);
      setRelatorioTipo(tipo);
      setRelatorioNome(nome);
      setRelatorioProgresso(0);
      setRelatorioMensagem(mensagensMotivacionais[0]);
      setRelatorioResultado(null);

      // Simular progresso
      let progresso = 0;
      let mensagemIndex = 0;

      const intervalo = setInterval(() => {
        progresso += Math.random() * 15 + 5; // Incremento aleatório entre 5-20%
        mensagemIndex = Math.floor(
          (progresso / 100) * mensagensMotivacionais.length
        );

        if (progresso >= 95) {
          progresso = 95; // Não deixar chegar a 100% até terminar
          clearInterval(intervalo);
        }

        setRelatorioProgresso(Math.min(progresso, 95));
        setRelatorioMensagem(
          mensagensMotivacionais[
            Math.min(mensagemIndex, mensagensMotivacionais.length - 1)
          ]
        );
      }, 800);

      // Guardar o intervalo para limpeza
      setRelatorioInterval(intervalo);
    },
    []
  );

  // ✅ FUNÇÃO PARA FINALIZAR GERAÇÃO DO RELATÓRIO
  const finalizarGeracaoRelatorio = useCallback(
    (sucesso, mensagem) => {
      // Limpar intervalo se existir
      if (relatorioInterval) {
        clearInterval(relatorioInterval);
        setRelatorioInterval(null);
      }

      setRelatorioProgresso(100);
      setRelatorioMensagem(
        sucesso ? "✅ Relatório gerado com sucesso!" : "❌ Erro na geração"
      );
      setRelatorioResultado({ sucesso, mensagem });

      // Fechar modal após 2 segundos se sucesso, ou manter aberto se erro
      setTimeout(() => {
        if (sucesso) {
          fecharModalRelatorio();
        }
      }, 2000);
    },
    [relatorioInterval]
  );

  // ✅ FUNÇÃO PARA FECHAR MODAL DE RELATÓRIO
  const fecharModalRelatorio = useCallback(() => {
    if (relatorioInterval) {
      clearInterval(relatorioInterval);
      setRelatorioInterval(null);
    }

    setRelatorioLoading(false);
    setRelatorioTipo("");
    setRelatorioNome("");
    setRelatorioProgresso(0);
    setRelatorioMensagem("");
    setRelatorioResultado(null);
  }, [relatorioInterval]);

  // Estados para filtros
  const [filtros, setFiltros] = useState({
    ano: new Date().getFullYear(),
    tipo: "Investimento",
    centroCusto: "",
    natureza: "",
  });

  // Estados para opções dos selects
  const [opcoesCentroCusto, setOpcoesCentroCusto] = useState([]);
  const [opcoesNatureza, setOpcoesNatureza] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingTotais, setLoadingTotais] = useState(false);

  // Estados dos modais
  const [modalIndex, setModalIndex] = useState(null);
  const [modalJustIndex, setModalJustIndex] = useState(null);
  const [modalListVisible, setModalListVisible] = useState(null);
  const [modalItensVisible, setModalItensVisible] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  // ✅ ADICIONAR ESTES ESTADOS NO INÍCIO DO COMPONENTE (após os outros estados)
  const [modalAnoAnteriorVisible, setModalAnoAnteriorVisible] = useState(null);
  const [dadosAnoAnterior, setDadosAnoAnterior] = useState(null);
  const [loadingAnoAnterior, setLoadingAnoAnterior] = useState(false);

  // Estados do novo modal moderno
  const [showInvestModal, setShowInvestModal] = useState(false);
  const [viewMode, setViewMode] = useState("table");
  const [novoInvestimento, setNovoInvestimento] = useState({
    IdOrcto: "",
    codigo: "",
    descricao: "",
    quantidade: 1,
    valor: 0,
    patrimonio: false,
    justificativa: "",
    responsavel: "",
    detalhamento: "",
    objetivo: "",
    justificativa2: "",
    linkProjeto: "",
    dataPrevista: "",
    categoria: "",
    status: "Proposto",
  });
  const [itensInvestimento, setItensInvestimento] = useState([]);
  const [editandoIndex, setEditandoIndex] = useState(null);
  const [modalContext, setModalContext] = useState(null);

  // 🔒 Trava a rolagem da página enquanto qualquer modal está aberto. Estes
  // modais são montados na mão (<div className="modal d-block">), sem o JS do
  // Bootstrap — e é ele quem normalmente põe modal-open no <body>.
  const algumModalAberto =
    modalVersaoVisible !== null ||
    modalIndex !== null ||
    modalJustIndex !== null ||
    modalListVisible !== null ||
    modalItensVisible !== null ||
    modalAnoAnteriorVisible !== null ||
    // showInvestModal, e não modalContext: este último é só o dado que
    // acompanha o modal de investimento, e nem todo caminho de fechamento o
    // zera — usá-lo deixaria a página travada com o modal já fechado.
    showInvestModal;

  useEffect(() => {
    if (!algumModalAberto) return;

    const overflowAnterior = document.body.style.overflow;
    const paddingAnterior = document.body.style.paddingRight;
    const larguraBarra = window.innerWidth - document.documentElement.clientWidth;

    document.body.style.overflow = "hidden";
    if (larguraBarra > 0) {
      document.body.style.paddingRight = `${larguraBarra}px`;
    }

    return () => {
      document.body.style.overflow = overflowAnterior;
      document.body.style.paddingRight = paddingAnterior;
    };
  }, [algumModalAberto]);

  // ✅ AGORA SIM: Calcular valores derivados APÓS declarar os estados
  const countNovos = itensInvestimento.filter(
    (item) => !item.dataInclusao
  ).length;

  const countModificados = itensInvestimento.filter(
    (item) => item.dataInclusao && item._modificado
  ).length;

  // Cálculos de paginação
  const itemsPerPage = 200;
  const paginatedData = data.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // ✅ CONFIGURAÇÃO DE STATUS PARA O NOVO MODAL
  const statusConfig = {
    Proposto: {
      icon: AlertCircle,
      color: "text-blue-600 bg-blue-50",
      border: "border-blue-200",
    },
    "Em Análise": {
      icon: Clock,
      color: "text-orange-600 bg-orange-50",
      border: "border-orange-200",
    },
    Aprovado: {
      icon: CheckCircle,
      color: "text-green-600 bg-green-50",
      border: "border-green-200",
    },
    Reprovado: {
      icon: XCircle,
      color: "text-red-600 bg-red-50",
      border: "border-red-200",
    },
  };

  // ✅ COMPONENTE DE STATUS BADGE
  const StatusBadge = ({ status }) => {
    const config = statusConfig[status];
    const IconComponent = config.icon;

    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color} ${config.border} border`}
      >
        <IconComponent size={12} />
        {status}
      </span>
    );
  };

  // ✅ FUNÇÃO PARA VALIDAR ITEM
  const validarItem = (item) => {
    const errors = {};

    if (!item.descricao.trim()) {
      errors.descricao = "Descrição é obrigatória";
    }

    if (!item.quantidade || item.quantidade <= 0) {
      errors.quantidade = "Quantidade deve ser maior que zero";
    }

    if (!item.valor || item.valor <= 0) {
      errors.valor = "Valor deve ser maior que zero";
    }

    if (!item.responsavel.trim()) {
      errors.responsavel = "Responsável é obrigatório";
    }

    if (!item.categoria.trim()) {
      errors.categoria = "Categoria é obrigatória";
    }

    return errors;
  };

  // ✅ FUNÇÃO ATUALIZADA PARA ADICIONAR ITEM COM VALIDAÇÃO
  const adicionarItem = () => {
    const errors = validarItem(novoInvestimento);
    setValidationErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    if (editandoIndex !== null) {
      const atualizados = [...itensInvestimento];
      const itemOriginal = atualizados[editandoIndex];

      atualizados[editandoIndex] = {
        ...novoInvestimento,
        id: itemOriginal.id || Date.now(),
        codigo: itemOriginal.codigo || `INV-${String(Date.now()).slice(-3)}`,
        // ✅ CORRIGIDO: SEMPRE marcar como modificado se já existir no banco
        _modificado: !!itemOriginal.dataInclusao, // ← SIMPLIFICAR ESTA LINHA
        // ✅ MANTER DADOS ORIGINAIS DO BANCO
        dataInclusao: itemOriginal.dataInclusao,
        usuario: itemOriginal.usuario,
      };

      setItensInvestimento(atualizados);
      setEditandoIndex(null);

      // ✅ LOG PARA DEBUG
      if (itemOriginal.dataInclusao) {
        console.log(
          `✅ Item existente marcado para atualização: ${novoInvestimento.descricao}`
        );
      } else {
        console.log(`✅ Item novo atualizado: ${novoInvestimento.descricao}`);
      }
    } else {
      // ✅ GARANTIR QUE SEMPRE TENHA UM CÓDIGO VÁLIDO
      const codigoGerado =
        novoInvestimento.codigo || `INV-${String(Date.now()).slice(-3)}`;

      setItensInvestimento([
        ...itensInvestimento,
        {
          ...novoInvestimento,
          id: Date.now(),
          codigo: codigoGerado, // ✅ USAR CÓDIGO GERADO
        },
      ]);

      console.log("✅ Item adicionado com código:", codigoGerado); // ✅ LOG PARA DEBUG
    }

    // Resetar campos
    setNovoInvestimento({
      IdOrcto: modalContext?.IdOrcto || "", // ✅ CORRIGIDO: Manter o IdOrcto do contexto
      codigo: "",
      descricao: "",
      quantidade: 1,
      valor: 0,
      patrimonio: false,
      justificativa: "",
      responsavel: "",
      detalhamento: "",
      objetivo: "",
      justificativa2: "",
      linkProjeto: "",
      dataPrevista: "",
      categoria: "",
      status: "Proposto",
    });
    setValidationErrors({});
  };

  const editarItem = (index) => {
    setNovoInvestimento(itensInvestimento[index]);
    setEditandoIndex(index);
  };

  const removerItem = (index) => {
    const novaLista = itensInvestimento.filter((_, i) => i !== index);
    setItensInvestimento(novaLista);

    // Se estava editando o item removido, limpar o estado de edição
    if (editandoIndex === index) {
      setEditandoIndex(null);
      setNovoInvestimento({
        IdOrcto: modalContext?.IdOrcto || "", // ✅ CORRIGIDO: Manter o IdOrcto do contexto
        codigo: "",
        descricao: "",
        quantidade: 1,
        valor: 0,
        patrimonio: false,
        justificativa: "",
        responsavel: "",
        detalhamento: "",
        objetivo: "",
        justificativa2: "",
        linkProjeto: "",
        dataPrevista: "",
        categoria: "",
        status: "Proposto",
      });
    }
  };

  // ✅ FUNÇÃO PARA CARREGAR ITENS DE INVESTIMENTO DO BANCO
  const carregarItensInvestimento = useCallback(
    async (centroCusto, natureza, ano) => {
      try {
        setLoadingItensInvestimento(true);
        const token = localStorage.getItem("authToken");
        if (!token) return;

        const PCODCOLIGADA = localStorage.getItem("PCODCOLIGADA");
        const PCODFILIAL = localStorage.getItem("PCODFILIAL");
        const tokenPayload = JSON.parse(atob(token.split(".")[1]));
        const currentUsername = tokenPayload.username || tokenPayload.id;

        const headers = {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        };

        // ✅ CONSTRUÇÃO DA WHERE CLAUSE PARA BUSCAR ITENS DE INVESTIMENTO
        let whereClause = `AND CODCOLIGADA = '${PCODCOLIGADA}' AND CODFILIAL = '${PCODFILIAL}' AND ANO = '${ano}' AND CODUSUARIO = '${currentUsername}'`;

        if (centroCusto && centroCusto !== "" && centroCusto !== "%") {
          whereClause += ` AND CODCCUSTO = '${centroCusto}'`;
        }

        if (natureza && natureza !== "" && natureza !== "%") {
          whereClause += ` AND CODTBORCAMENTO = '${natureza}'`;
        }

        console.log(
          "🔍 Carregando itens de investimento com WHERE:",
          whereClause
        );

        const response = await fetch(
          `${API_BASE_URL}/totvs-sql/sql_MAN_ORC_0010/filter`,
          {
            method: "POST",
            headers,
            body: JSON.stringify({
              WHERE: whereClause,
              ORDER_BY: "DATAINCLUSAO DESC, DESCRICAO",
            }),
          }
        );

        const result = await response.json();
        console.log("📋 Resultado dos itens de investimento:", result);

        if (result.success && result.data) {
          // ✅ MAPEAR DADOS DOS ITENS DE INVESTIMENTO PARA O FORMATO DO FRONTEND
          const itensMapeados = result.data.map((item, index) => ({
            id: item.ID,
            codigo: item.CODIGO || `INV-${String(Date.now()).slice(-3)}`,
            descricao: item.DESCRICAO || "",
            quantidade: parseFloat(item.QUANTIDADE || 1),
            valor: parseFloat(item.VALORUNITARIO || 0),
            patrimonio:
              item.PATRIMONIO === 1 ||
              item.PATRIMONIO === true ||
              item.PATRIMONIO === "true",
            justificativa: item.JUSTIFICATIVA || "",
            responsavel: item.RESPONSAVEL || "",
            detalhamento: item.DETALHAMENTO || "",
            objetivo: item.OBJETIVO || "",
            justificativa2: item.JUSTIFICATIVA || "",
            linkProjeto: item.LINKPROJETO || "",

            dataPrevista: item.DATAPREVISTA.split("T")[0],

            categoria: item.CATEGORIA || "",
            status: item.STATUS || "Proposto",
            // Dados adicionais do banco
            dataInclusao: item.DATAINCLUSAO,
            usuario: item.CODUSUARIO,
            IdOrcto: item.IDORCTO, // ✅ CORRIGIDO: Mapear corretamente
          }));

          console.log("✅ Itens de investimento mapeados:", itensMapeados);
          setItensInvestimento(itensMapeados);
        } else {
          console.log("⚠️ Nenhum item de investimento encontrado");
          setItensInvestimento([]);
        }
      } catch (error) {
        console.error("❌ Erro ao carregar itens de investimento:", error);
        setItensInvestimento([]);
      } finally {
        setLoadingItensInvestimento(false);
      }
    },
    []
  );

  // ✅ SALVANDO ITENS - FUNÇÃO ATUALIZADA COM SUPORTE A ITENS MODIFICADOS
  const salvarTodosItens = async () => {
    if (itensInvestimento.length === 0) {
      alert("⚠️ Adicione pelo menos um item antes de salvar!");
      return;
    }

    setSalvandoItens(true);

    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        alert("❌ Token não encontrado");
        return;
      }

      const tokenPayload = JSON.parse(atob(token.split(".")[1]));
      const currentUsername = tokenPayload.username || tokenPayload.id;
      const selectedYear = localStorage.getItem("selectedYear");
      const PCODCOLIGADA = localStorage.getItem("PCODCOLIGADA");
      const PCODFILIAL = localStorage.getItem("PCODFILIAL");

      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };

      // ✅ LINHA ~1195 - Separar itens novos dos existentes modificados
      const itensNovos = itensInvestimento.filter((item) => !item.dataInclusao);
      const itensExistentes = itensInvestimento.filter(
        (item) => item.dataInclusao && item._modificado // ← ADICIONAR && item._modificado
      );

      // ✅ VALIDAR SE TEM IdOrcto NOS ITENS NOVOS
      const itensComIdOrcto = itensNovos.filter(
        (item) => item.IdOrcto && item.IdOrcto !== ""
      );

      if (itensNovos.length > 0 && itensComIdOrcto.length === 0) {
        alert(
          "❌ Erro: IdOrcto não encontrado. Feche e abra o modal novamente."
        );
        console.error("❌ Itens sem IdOrcto:", itensNovos);
        return;
      }

      // Salvar apenas itens novos
      if (itensNovos.length > 0) {
        // Preparar dados para envio
        const dadosParaEnvio = {
          itens: itensNovos.map((item) => {
            const itemFormatado = {
              IDORCTO: parseInt(item.IdOrcto, 10),
              Codigo: String(item.codigo || ""),
              Descricao: String(item.descricao || ""),
              Quantidade: parseInt(item.quantidade, 10),
              ValorUnitario: parseFloat(item.valor),
              ValorTotal: parseFloat(item.quantidade * item.valor),
              Patrimonio: Boolean(item.patrimonio),
              Responsavel: String(item.responsavel || ""),
              Detalhamento: String(item.detalhamento || ""),
              Objetivo: String(item.objetivo || ""),
              Justificativa: String(item.justificativa2 || ""),
              LinkProjeto: String(item.linkProjeto || ""),
              DataPrevista: new Date(item.dataPrevista)
                .toISOString()
                .split("T")[0],
              Categoria: String(item.categoria || ""),
              Status: String(item.status || "Proposto"),
              Ano: parseInt(selectedYear, 10),
              CodColigada: parseInt(PCODCOLIGADA, 10),
              CodFilial: parseInt(PCODFILIAL, 10),
              RECCREATEDBY: String(currentUsername),
              RECCREATEDON: new Date().toISOString().split("T")[0],
              RECMODIFIEDBY: String(currentUsername),
              RECMODIFIEDON: new Date().toISOString().split("T")[0],
            };

            return itemFormatado;
          }),
        };

        console.log("💾 Enviando dados para API:", dadosParaEnvio);

        const response = await fetch(
          `${API_BASE_URL}/man_orc_orcto_itinvest/`,
          {
            method: "POST",
            headers,
            body: JSON.stringify(dadosParaEnvio),
          }
        );

        const result = await response.json();

        if (response.ok) {
          console.log("✅ Itens salvos com sucesso:", result);
          alert(`✅ ${itensNovos.length} novos itens salvos com sucesso!`);
        } else {
          console.error("❌ Erro ao salvar itens:", result);
          alert(
            `❌ Erro ao salvar itens: ${result.message || "Erro desconhecido"}`
          );
          return;
        }
      }

      // ✅ LINHA ~1270 - Após salvar itens novos, adicionar salvamento dos modificados:
      // Salvar itens modificados
      if (itensExistentes.length > 0) {
        console.log(
          `🔄 Atualizando ${itensExistentes.length} itens modificados...`
        );

        for (const item of itensExistentes) {
          const resultado = await atualizarItemInvestimento(item, item);
          if (!resultado.success) {
            alert(
              `❌ Erro ao atualizar item "${item.descricao}": ${resultado.message}`
            );
            return;
          }
        }

        alert(`✅ ${itensExistentes.length} itens atualizados com sucesso!`);
      }

      // ✅ LOG DE FEEDBACK
      if (itensExistentes.length === 0 && itensNovos.length === 0) {
        console.log("ℹ️ Nenhum item novo ou modificado para salvar");
      }

      // Limpar apenas o formulário de novo item
      setNovoInvestimento({
        IdOrcto: modalContext?.IdOrcto || "", // ✅ CORRIGIDO: Manter o IdOrcto do contexto
        codigo: "",
        descricao: "",
        quantidade: 1,
        valor: 0,
        patrimonio: false,
        justificativa: "",
        responsavel: "",
        detalhamento: "",
        objetivo: "",
        justificativa2: "",
        linkProjeto: "",
        dataPrevista: "",
        categoria: "",
        status: "Proposto",
      });
      setEditandoIndex(null);
      setValidationErrors({});

      // Recarregar os itens do banco para mostrar os dados atualizados
      if (modalContext) {
        await carregarItensInvestimento(
          modalContext.centroCusto,
          modalContext.natureza,
          modalContext.ano
        );
      }

      // Atualizar dados principais se necessário
      await atualizarTotais();
      await carregarDadosOrcamento();
      

    } catch (error) {
      console.error("❌ Erro na requisição:", error);
      alert(`❌ Erro de conexão: ${error.message}`);
    } finally {
      setSalvandoItens(false);
    }
  };

  //✅ NOVA FUNÇÃO PARA ATUALIZAR ITEM DE INVESTIMENTO - CORRIGIDA
  const atualizarItemInvestimento = async (item, dadosAtualizados) => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        alert("❌ Token não encontrado");
        return { success: false, message: "Token não encontrado" };
      }

      const currentUsername =
        JSON.parse(atob(token.split(".")[1])).username ||
        JSON.parse(atob(token.split(".")[1])).id;
      const selectedYear = localStorage.getItem("selectedYear");
      const PCODCOLIGADA = localStorage.getItem("PCODCOLIGADA");
      const PCODFILIAL = localStorage.getItem("PCODFILIAL");

      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };

      // ✅ LINHA ~1340 - Corrigir o payload de atualização
      const dadosParaUpdate = {
        IDORCTO: parseInt(dadosAtualizados.IdOrcto, 10),
        CODIGO: String(dadosAtualizados.codigo || ""),
        DESCRICAO: String(dadosAtualizados.descricao || ""),
        QUANTIDADE: parseInt(dadosAtualizados.quantidade, 10),
        VALORUNITARIO: parseFloat(dadosAtualizados.valor),
        VALORTOTAL: parseFloat(
          dadosAtualizados.quantidade * dadosAtualizados.valor
        ),
        PATRIMONIO: Boolean(dadosAtualizados.patrimonio),
        RESPONSAVEL: String(dadosAtualizados.responsavel || ""),
        DETALHAMENTO: String(dadosAtualizados.detalhamento || ""),
        OBJETIVO: String(dadosAtualizados.objetivo || ""),
        JUSTIFICATIVA: String(dadosAtualizados.justificativa2 || ""), // ← USAR justificativa2
        LINKPROJETO: String(dadosAtualizados.linkProjeto || ""),
        DATAPREVISTA: dadosAtualizados.dataPrevista || null,
        CATEGORIA: String(dadosAtualizados.categoria || ""),
        STATUS: String(dadosAtualizados.status || "Proposto"),
        ANO: parseInt(selectedYear, 10),
        CODCOLIGADA: parseInt(PCODCOLIGADA, 10),
        CODFILIAL: parseInt(PCODFILIAL, 10),
        RECMODIFIEDBY: String(currentUsername),
        RECMODIFIEDON: new Date().toISOString(),
        // ✅ REMOVER esta linha se não for necessária para a API
        InvestimentoID: item.id,
      };

      console.log("🔄 Atualizando item ID:", item.id);
      console.log("📝 Dados para atualização:", dadosParaUpdate);

      const response = await fetch(`${API_BASE_URL}/man_orc_orcto_itinvest/`, {
        method: "PUT",
        headers,
        body: JSON.stringify(dadosParaUpdate),
      });

      const result = await response.json();

      if (response.ok) {
        console.log("✅ Item atualizado com sucesso:", result);
        return { success: true, data: result };
      } else {
        console.error("❌ Erro ao atualizar item:", result);
        return {
          success: false,
          message: result.message || "Erro desconhecido",
        };
      }
    } catch (error) {
      console.error("❌ Erro na requisição de atualização:", error);
      return { success: false, message: error.message };
    }
  };

  // ✅ NOVA FUNÇÃO PARA EXCLUIR ITEM DE INVESTIMENTO
  const excluirItemInvestimento = async (item, index) => {
    // 1. Se ainda não foi salvo no banco, remove localmente sem pedir confirmação
    if (!item.dataInclusao) {
      removerItem(index);
      return;
    }

    // 2. Pergunta de confirmação
    const confirmacao = window.confirm(
      `Tem certeza que deseja excluir o item "${item.descricao}"?`
    );
    if (!confirmacao) return;

    try {
      // 3. Pega o token
      const token = localStorage.getItem("authToken");
      if (!token) {
        alert("❌ Token não encontrado");
        return;
      }

      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };

      // 4. Requisição DELETE

      const response = await fetch(`${API_BASE_URL}/man_orc_orcto_itinvest/`, {
        method: "DELETE",
        headers,
        body: JSON.stringify({ InvestimentoID: item.id }),
      });

      const result = await response.json();

      // 5. Se deu certo, atualiza estado e limpa edição
      if (response.ok) {
        alert("✅ Item excluído com sucesso!");
        // Remove da lista
        setItensInvestimento((prev) => prev.filter((_, i) => i !== index));
        // Se estava editando este item, limpa o formulário de edição
        if (editandoIndex === index) {
          setEditandoIndex(null);
          setNovoInvestimento({
            IdOrcto: modalContext?.IdOrcto || "",
            codigo: "",
            descricao: "",
            quantidade: 1,
            valor: 0,
            patrimonio: false,
            justificativa: "",
            responsavel: "",
            detalhamento: "",
            objetivo: "",
            justificativa2: "",
            linkProjeto: "",
            dataPrevista: "",
            categoria: "",
            status: "Proposto",
          });
        }

      // Atualizar dados principais se necessário
      await atualizarTotais();
      await carregarDadosOrcamento();


       
      } else {
        // 6. Erro de validação do servidor
        console.error("❌ Erro ao excluir item:", result);
        alert(`❌ Erro ao excluir item: ${result.message || "Desconhecido"}`);
      }
    } catch (error) {
      // 7. Erro de rede ou imprevisto
      console.error("❌ Erro na requisição de exclusão:", error);
      alert(`❌ Erro de conexão: ${error.message}`);
    }
  };

  // ✅ FUNÇÃO SIMPLES PARA ATUALIZAR ORÇAMENTO
  const atualizarValorOrcamento = async (
    idOrcto,
    novoValor,
    usuario,
    dataAlteracao
  ) => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        return { success: false, message: "Token não encontrado" };
      }

      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };

      const dados = {
        IdOrcto: idOrcto,
        valor: novoValor,
        usuario: usuario,
        dataAlteracao: dataAlteracao,
      };

      console.log("💾 Salvando alteração:", dados);

      const response = await fetch(
        `${API_BASE_URL}/SQlCustom/SQLCustomUpdate`,
        {
          method: "POST",
          headers,
          body: JSON.stringify(dados),
        }
      );

      const result = await response.json();

      if (response.ok) {
        console.log("✅ Valor atualizado com sucesso");
        return { success: true, data: result };
      } else {
        console.error("❌ Erro ao atualizar:", result);
        return { success: false, message: result.error || "Erro desconhecido" };
      }
    } catch (error) {
      console.error("❌ Erro na requisição:", error);
      return { success: false, message: error.message };
    }
  };

  // ✅ FUNÇÃO PARA ATUALIZAR OBSERVAÇÃO
  const atualizarObservacao = useCallback(
    async (idOrcto, novaObservacao, usuario, dataAlteracao) => {
      try {
        const token = localStorage.getItem("authToken");
        if (!token) {
          return { success: false, message: "Token não encontrado" };
        }

        const headers = {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        };

        const dados = {
          IdOrcto: idOrcto,
          observacao: novaObservacao,
          usuario: usuario,
          dataAlteracao: dataAlteracao,
        };

        console.log("💾 Salvando observação:", dados);

        const response = await fetch(
          `${API_BASE_URL}/SQlCustom/SQLCustomUpdateObs`,
          {
            method: "POST",
            headers,
            body: JSON.stringify(dados),
          }
        );

        const result = await response.json();

        if (response.ok) {
          console.log("✅ Observação atualizada com sucesso");
          return { success: true, data: result };
        } else {
          console.error("❌ Erro ao atualizar observação:", result);
          return {
            success: false,
            message: result.error || "Erro desconhecido",
          };
        }
      } catch (error) {
        console.error("❌ Erro na requisição de observação:", error);
        return { success: false, message: error.message };
      }
    },
    []
  );

  // ✅ NOVA FUNÇÃO PARA ATUALIZAR ALERTA
  const atualizarAlerta = useCallback(
    async (
      idOrcto,
      alert25,
      alert50,
      alert75,
      alert100,
      usuario,
      dataAlteracao
    ) => {
      try {
        const token = localStorage.getItem("authToken");
        if (!token) {
          return { success: false, message: "Token não encontrado" };
        }

        const headers = {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        };

        const dados = {
          IdOrcto: idOrcto,
          alert25: alert25,
          alert50: alert50,
          alert75: alert75,
          alert100: alert100,
          usuario: usuario,
          dataAlteracao: dataAlteracao,
        };

        console.log("💾 Salvando alertas:", dados);

        const response = await fetch(
          `${API_BASE_URL}/SQlCustom/SQLCustomUpdateAlert`,
          {
            method: "POST",
            headers,
            body: JSON.stringify(dados),
          }
        );

        const result = await response.json();

        if (response.ok) {
          console.log("✅ Alertas atualizados com sucesso");
          return { success: true, data: result };
        } else {
          console.error("❌ Erro ao atualizar alertas:", result);
          return {
            success: false,
            message: result.error || "Erro desconhecido",
          };
        }
      } catch (error) {
        console.error("❌ Erro na requisição de alertas:", error);
        return { success: false, message: error.message };
      }
    },
    []
  );

  // ✅ 1. ADICIONE ESTA FUNÇÃO NO INÍCIO DO COMPONENTE (após os states)
  const podeEditarOrcamento = () => {
    const anoSelecionado = parseInt(
      localStorage.getItem("selectedYear") || new Date().getFullYear()
    );
    return anoSelecionado >= localStorage.getItem("selectedTrava"); //anoSelecionado > 2025; // Só permite edição para anos futuros (2026+)
  };

  // ✅ 2. ADICIONE ESTA FUNÇÃO PARA VALIDAR ANTES DE SALVAR
  const validarAnoEdicao = () => {
    const anoSelecionado = parseInt(
      localStorage.getItem("selectedYear") || new Date().getFullYear()
    );
    if (anoSelecionado <= (localStorage.getItem("selectedTrava") - 1) /*2025*/) {
      alert(
        `⚠️ Não é possível alterar orçamentos para o ano ${anoSelecionado}.\nApenas anos futuros (${localStorage.getItem("selectedTrava")} em diante) podem ser editados.`
      );
      return false;
    }
    return true;
  };

  // ✅ 3. MODIFIQUE A FUNÇÃO handleOrcadoChange PARA INCLUIR A VALIDAÇÃO
  const handleOrcadoChange = async (index, newValue) => {
    // ✅ VALIDAÇÃO DE ANO ADICIONADA
    if (!validarAnoEdicao()) {
      // Restaurar valor original se ano inválido
      const globalIndex = (currentPage - 1) * itemsPerPage + index;
      const item = data[globalIndex];
      const inputElement = document.activeElement;
      if (inputElement) {
        inputElement.value = `R$${item.orcado.toFixed(2)}`;
        inputElement.blur();
      }
      return;
    }

    const globalIndex = (currentPage - 1) * itemsPerPage + index;
    const newData = [...data];
    const item = newData[globalIndex];
    const oldValue = item.orcado;
    const valorNumerico =
      parseFloat(newValue.replace(/[^\d,]/g, "").replace(",", ".")) || 0;

    // Verificar se o valor realmente mudou
    if (Math.abs(valorNumerico - oldValue) < 0.01) {
      console.log("📋 Valor não alterado");
      return;
    }

    // Feedback visual simples
    const inputElement = document.activeElement;
    if (inputElement) {
      inputElement.style.backgroundColor = "#fff3cd";
      inputElement.disabled = true;
    }

    try {
      // 🔄 CHAMADA SIMPLES PARA ATUALIZAR
      const token = localStorage.getItem("authToken");
      const tokenPayload = JSON.parse(atob(token.split(".")[1]));
      const currentUsername = tokenPayload.username || tokenPayload.id;
      const agora = new Date().toISOString();

      const resultado = await atualizarValorOrcamento(
        item.IdOrcto,
        valorNumerico,
        currentUsername,
        agora
      );

      if (resultado.success) {
        // ✅ SUCESSO - Atualizar estado local
        newData[globalIndex].orcado = valorNumerico;
        newData[globalIndex].resultado =
          valorNumerico - item.realizado - item.empenhado;
        setData(newData);

        // Feedback visual de sucesso
        if (inputElement) {
          inputElement.style.backgroundColor = "#d1edff";
          inputElement.style.border = "2px solid #28a745";
          inputElement.value = `R$${valorNumerico.toFixed(2)}`;

          setTimeout(() => {
            inputElement.style.backgroundColor = "";
            inputElement.style.border = "";
            inputElement.disabled = false;
          }, 1500);
        }

        // Atualizar totais
        await atualizarTotais();

        console.log("✅ Valor salvo com sucesso!");
      } else {
        // ❌ ERRO - Reverter
        if (inputElement) {
          inputElement.style.backgroundColor = "#f8d7da";
          inputElement.style.border = "2px solid #dc3545";
          inputElement.value = `R$${oldValue.toFixed(2)}`;

          setTimeout(() => {
            inputElement.style.backgroundColor = "";
            inputElement.style.border = "";
            inputElement.disabled = false;
          }, 2000);
        }

        alert(`❌ Erro: ${resultado.message}`);
      }
    } catch (error) {
      console.error("❌ Erro inesperado:", error);

      // Reverter em caso de erro
      if (inputElement) {
        inputElement.style.backgroundColor = "#f8d7da";
        inputElement.value = `R$${oldValue.toFixed(2)}`;
        inputElement.disabled = false;
      }

      alert(`❌ Erro: ${error.message}`);
    }
  };

  // ✅ FUNÇÃO PARA SALVAR OBSERVAÇÃO
  const salvarObservacao = useCallback(async () => {
    if (modalJustIndex === null) return;

    setSalvandoObservacao(true);

    try {
      const globalIndex = (currentPage - 1) * itemsPerPage + modalJustIndex;
      const item = data[globalIndex];
      const novaObservacao = item.justificativa || "";

      // Obter dados do usuário
      const token = localStorage.getItem("authToken");
      const tokenPayload = JSON.parse(atob(token.split(".")[1]));
      const currentUsername = tokenPayload.username || tokenPayload.id;
      const agora = new Date().toISOString();

      const resultado = await atualizarObservacao(
        item.IdOrcto,
        novaObservacao,
        currentUsername,
        agora
      );

      if (resultado.success) {
        // ✅ SUCESSO - Atualizar estado local
        const newData = [...data];
        newData[globalIndex].obs = novaObservacao;
        setData(newData);

        alert("✅ Observação salva com sucesso!");
        closeJustModal();
      } else {
        alert(`❌ Erro ao salvar observação: ${resultado.message}`);
      }
    } catch (error) {
      console.error("❌ Erro inesperado:", error);
      alert(`❌ Erro: ${error.message}`);
    } finally {
      setSalvandoObservacao(false);
    }
  }, [modalJustIndex, currentPage, itemsPerPage, data, atualizarObservacao]);

  // ✅ NOVA FUNÇÃO PARA SALVAR ALERTA
  const salvarAlerta = useCallback(async () => {
    if (modalIndex === null) return;

    setSalvandoAlerta(true);

    try {
      const globalIndex = (currentPage - 1) * itemsPerPage + modalIndex;
      const item = data[globalIndex];

      // Obter dados do usuário
      const token = localStorage.getItem("authToken");
      const tokenPayload = JSON.parse(atob(token.split(".")[1]));
      const currentUsername = tokenPayload.username || tokenPayload.id;
      const agora = new Date().toISOString();

      const resultado = await atualizarAlerta(
        item.IdOrcto,
        item.email25 || "",
        item.email50 || "",
        item.email75 || "",
        item.email100 || "",
        currentUsername,
        agora
      );

      if (resultado.success) {
        alert("✅ Alertas salvos com sucesso!");
        closeModal();
      } else {
        alert(`❌ Erro ao salvar alertas: ${resultado.message}`);
      }
    } catch (error) {
      console.error("❌ Erro inesperado:", error);
      alert(`❌ Erro: ${error.message}`);
    } finally {
      setSalvandoAlerta(false);
    }
  }, [modalIndex, currentPage, itemsPerPage, data, atualizarAlerta]);

  // ✅ FUNÇÃO PARA FORMATAR DATAS
  const formatarData = (dataString) => {
    try {
      // Se for uma string no formato ISO (2025-04-16T00:00:00.000Z)
      if (typeof dataString === "string" && dataString.includes("T")) {
        const data = new Date(dataString);
        return data.toLocaleDateString("pt-BR");
      }

      // Se for uma string no formato YYYY-MM-DD
      if (typeof dataString === "string" && dataString.includes("-")) {
        const [ano, mes, dia] = dataString.split("T")[0].split("-");
        return `${dia}/${mes}/${ano}`;
      }

      // Se for um objeto Date
      if (dataString instanceof Date) {
        return dataString.toLocaleDateString("pt-BR");
      }

      // Tentativa genérica
      const data = new Date(dataString);
      if (!isNaN(data.getTime())) {
        return data.toLocaleDateString("pt-BR");
      }

      return "Data inválida";
    } catch (error) {
      console.warn("Erro ao formatar data:", dataString, error);
      return "Data inválida";
    }
  };

  // ✅ FUNÇÃO PARA CARREGAR MOVIMENTOS
  const carregarMovimentos = useCallback(async (centroCusto, natureza, ano) => {
    try {
      setLoadingMovimentos(true);
      const token = localStorage.getItem("authToken");
      if (!token) return;

      const PCODCOLIGADA = localStorage.getItem("PCODCOLIGADA");
      const PCODFILIAL = localStorage.getItem("PCODFILIAL");

      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };

      // ✅ CONSTRUÇÃO DA WHERE CLAUSE PARA MOVIMENTOS
      let whereClause = `AND CODCOLIGADA = '${PCODCOLIGADA}' AND CODFILIAL = '${PCODFILIAL}' AND ANO = '${ano}'`;

      if (centroCusto && centroCusto !== "" && centroCusto !== "%") {
        whereClause += ` AND CODCCUSTO = '${centroCusto}'`;
      }

      if (natureza && natureza !== "" && natureza !== "%") {
        whereClause += ` AND CODTBORCAMENTO = '${natureza}'`;
      }

      console.log("🔍 Carregando movimentos com WHERE:", whereClause);

      const response = await fetch(
        `${API_BASE_URL}/totvs-sql/sql_MAN_ORC_0019/filter`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            WHERE: whereClause,
            ORDER_BY: "DTORIG, FORNECEDOR",
          }),
        }
      );

      const result = await response.json();
      console.log("📋 Resultado dos movimentos:", result);

      if (result.success && result.data) {
        // ✅ MAPEAR DADOS DOS MOVIMENTOS
        const movimentosMapeados = result.data.map((item) => ({
          idMov: item.IDMOVDEST || item.IDMOVORG || "N/A",
          data: item.DTORIG ? formatarData(item.DTORIG) : "N/A",
          dataOrig: item.DTORIG || null, // data crua, usada para ordenar
          fornecedor: item.FORNECEDOR || "N/A",
          valor: parseFloat(item.LIQUIDO || item.VALORPATIO || 0),
          origem: item.ORIGEM || "N/A",
          destino: item.DESTINO || "N/A",
          tipo: item.TIPO || "N/A",
          status: item.FLANSTATUS || item.TMOVSTATUS || "N/A",
          empenhado: parseFloat(item.EMPENHADO || 0),
          // Dados adicionais para drill-down
          idMovOrg: item.IDMOVORG,
          idMovDest: item.IDMOVDEST,
          codCCusto: item.CODCCUSTO,
          codTbOrcamento: item.CODTBORCAMENTO,
          idContrato: item.IDCONTRATO,
        }));

        setMovimentosData(movimentosMapeados);
      } else {
        console.log("⚠️ Nenhum movimento encontrado");
        setMovimentosData([]);
      }
    } catch (error) {
      console.error("❌ Erro ao carregar movimentos:", error);
      setMovimentosData([]);
    } finally {
      setLoadingMovimentos(false);
    }
  }, []);

  // ✅ FUNÇÃO PARA CARREGAR ITENS DO MOVIMENTO
  const carregarItensMovimento = useCallback(async (idMovimento, centroCusto, natureza) => {
    try {
      setLoadingItens(true);
      const token = localStorage.getItem("authToken");
      if (!token) return;

      const PCODCOLIGADA = localStorage.getItem("PCODCOLIGADA");
      const PCODFILIAL = localStorage.getItem("PCODFILIAL");

      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };

      // ✅ CONSTRUÇÃO DA WHERE CLAUSE PARA ITENS DO MOVIMENTO
      let whereClause = `AND CODCOLIGADA = '${PCODCOLIGADA}' AND CODFILIAL = '${PCODFILIAL}'`;

      /*if (idMovimento && idMovimento !== "N/A") {
        whereClause += ` AND IDMOVDEST = '${idMovimento}'  AND CODCCUSTO = '${centroCusto}' AND CODTBORCAMENTO = '${natureza}' `;
      }*/

      if (idMovimento && idMovimento !== "N/A") {
        whereClause += ` AND IDMOVDEST = '${idMovimento}'  AND CODCCUSTO = '${centroCusto}' `;
      }

      console.log("🔍 Carregando itens do movimento com WHERE:", whereClause);
      console.log("📦 ID do movimento:", idMovimento);

      const response = await fetch(
        `${API_BASE_URL}/totvs-sql/sql_MAN_ORC_0008/filter`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            WHERE: whereClause,
            ORDER_BY: "DESCRICAO, QUANTIDADE",
          }),
        }
      );

      const result = await response.json();
      console.log("📦 Resultado dos itens:", result);

      if (result.success && result.data) {
        // ✅ MAPEAR DADOS DOS ITENS
        const itensMapeados = result.data.map((item) => ({
          idItem: item.IDITEM || item.CODPRD || "N/A",
          descricao: item.DESCRICAO || item.NOMEPRD || "N/A",
          quantidade: parseFloat(item.QUANTIDADE || 0),
          valorUnitario: parseFloat(item.VALORUNITARIO || item.PRECO || 0),
          valorTotal: parseFloat(item.VALORTOTAL || item.VALOR || 0),
          unidade: item.UNIDADE || item.CODUND || "UN",
          observacao: item.OBSERVACAO || item.OBS || "",
          // Referências
          idMovimento: item.IDMOVDEST || item.IDMOVORG,
          numeroNF: item.NUMERONF || item.NRONOTA || "N/A",
          serieNF: item.SERIE || item.SERIE || "N/A",
        }));

        setItensMovimento(itensMapeados);
      } else {
        console.log("⚠️ Nenhum item encontrado para o movimento");
        setItensMovimento([]);
      }
    } catch (error) {
      console.error("❌ Erro ao carregar itens do movimento:", error);
      setItensMovimento([]);
    } finally {
      setLoadingItens(false);
    }
  }, []);

  // 📊 FUNÇÃO PARA CARREGAR TOTAIS (CORRIGIDA PARA TRATAR ARRAY)
  const carregarTotaisOrcamento = useCallback(async (whereClause) => {
    try {
      setLoadingTotais(true);
      const token = localStorage.getItem("authToken");
      if (!token) return;

      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };

      console.log("🔄 Carregando totais com WHERE:", whereClause);
      const PCODCOLIGADA = localStorage.getItem("PCODCOLIGADA");
      const PCODFILIAL = localStorage.getItem("PCODFILIAL");
      const selectedYear = localStorage.getItem("selectedYear");

      const response = await fetch(
        `${API_BASE_URL}/totvs-sql/sql_MAN_ORC_0005/filter`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            PARAM: [
              {
                CODFILIAL: PCODFILIAL,
                CODCOLIGADA: PCODCOLIGADA,
                ANO: selectedYear,
              },
            ],

            WHERE: whereClause,
            SELECT: `
              SUM(COALESCE(ORCADO, 0)) as totalOrcado,
              SUM(COALESCE(REALIZADO, 0)) as totalRealizado,
              SUM(COALESCE(ECONOMIA, 0)) as totalEconomia
            `,
            GROUP_BY: "1",
          }),
        }
      );

      const result = await response.json();
      console.log("📊 Resultado dos totais:", result);

      if (result.success && result.data && result.data.length > 0) {
        // 🔧 VERIFICAR SE É UM ÚNICO RESULTADO AGREGADO OU ARRAY
        if (
          result.data.length === 1 &&
          result.data[0].totalOrcado !== undefined
        ) {
          // Resultado já agregado pela API
          const totais = {
            orcado: parseFloat(result.data[0].totalOrcado || 0),
            realizado: parseFloat(result.data[0].totalRealizado || 0),
            economia: parseFloat(result.data[0].totalEconomia || 0),
            empenhado: parseFloat(result.data[0].empenhado || 0),
          };

          totais.Consumo =
            totais.orcado > 0 ? (totais.realizado / totais.orcado) * 100 : 0;
          console.log("📈 Totais da API (agregado):", totais);
          setDataTotal([totais]);
        } else {
          // 🛠️ FALLBACK: SOMAR NO CLIENTE SE API RETORNAR ARRAY
          console.log("⚠️ API retornou array, somando no cliente...");
          const totais = result.data.reduce(
            (acc, item) => ({
              orcado: acc.orcado + parseFloat(item.ORCADO || 0),
              realizado: acc.realizado + parseFloat(item.REALIZADO || 0),
              economia: acc.economia + parseFloat(item.ECONOMIA || 0),
              empenhado: acc.empenhado + parseFloat(item.EMPENHADO || 0),
            }),
            { orcado: 0, realizado: 0, economia: 0, empenhado: 0 }
          );

          totais.Consumo =
            totais.orcado > 0 ? (totais.realizado / totais.orcado) * 100 : 0;
          console.log("📈 Totais calculados no cliente:", totais);
          setDataTotal([totais]);
        }
      } else {
        console.log("⚠️ Nenhum dado de totais retornado");
        setDataTotal([{ orcado: 0, realizado: 0, economia: 0, Consumo: 0 }]);
      }
    } catch (error) {
      console.error("❌ Erro ao carregar totais:", error);
      setDataTotal([{ orcado: 0, realizado: 0, economia: 0, Consumo: 0 }]);
    } finally {
      setLoadingTotais(false);
    }
  }, []);

  // 🔄 FUNÇÃO PARA ATUALIZAR TOTAIS COM useCallback
  const atualizarTotais = useCallback(async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;

      const tokenPayload = JSON.parse(atob(token.split(".")[1]));
      const currentUsername = tokenPayload.username || tokenPayload.id;
      const selectedYear = localStorage.getItem("selectedYear");
      const selectedFilial = localStorage.getItem("selectedFilial");
      const PCODCOLIGADA = localStorage.getItem("PCODCOLIGADA");
      const PCODFILIAL = localStorage.getItem("PCODFILIAL");

      let whereClause = `AND TIPO = 'Investimento' AND selectedFilial ='${selectedFilial}' AND ANO ='${selectedYear}' AND CODUSUARIO='${currentUsername}' 
                         AND CODCOLIGADA = '${PCODCOLIGADA}' AND CODFILIAL ='${PCODFILIAL}' `;

      if (
        filtros.centroCusto &&
        filtros.centroCusto !== "" &&
        filtros.centroCusto !== "%"
      ) {
        whereClause += ` AND CODCCUSTO = '${filtros.centroCusto}'`;
      }

      if (
        filtros.natureza &&
        filtros.natureza !== "" &&
        filtros.natureza !== "%"
      ) {
        whereClause += ` AND CODTBORCAMENTO = '${filtros.natureza}'`;
      }

      console.log("🎯 Atualizando totais para centro:", filtros.centroCusto);
      console.log("🔍 WHERE clause:", whereClause);

      await carregarTotaisOrcamento(whereClause);
    } catch (error) {
      console.error("❌ Erro ao atualizar totais:", error);
    }
  }, [filtros, carregarTotaisOrcamento]);

  // 🏢 FUNÇÃO PARA CARREGAR OPÇÕES DE CENTRO DE CUSTO (CORRIGIDA)
  const carregarOpcoesCentroCusto = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;

      const tokenPayload = JSON.parse(atob(token.split(".")[1]));
      const currentUsername = tokenPayload.username || tokenPayload.id;
      const selectedYear = localStorage.getItem("selectedYear");
      const selectedFilial = localStorage.getItem("selectedFilial");

      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };

      const response = await fetch(
        `${API_BASE_URL}/totvs-sql/sql_MAN_ORC_0006/filter`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            WHERE: `AND TIPO = 'Investimento' AND selectedFilial ='${selectedFilial}' AND ANO ='${selectedYear}' AND CODUSUARIO='${currentUsername}'  `,
            ORDER_BY: "CENTROCUSTO",
            DISTINCT: "CODCCUSTO, CENTROCUSTO",
          }),
        }
      );

      const result = await response.json();
      if (result.success && result.data) {
        // ✅ REMOÇÃO DE DUPLICATAS NO FRONTEND
        const centrosCustoUnicos = result.data.reduce((acc, item) => {
          const existe = acc.find((cc) => cc.CODCCUSTO === item.CODCCUSTO);
          if (!existe) {
            acc.push(item);
          }
          return acc;
        }, []);

        setOpcoesCentroCusto(centrosCustoUnicos);
      }
    } catch (error) {
      console.error("Erro ao carregar centros de custo:", error);
    }
  };

  // 📋 FUNÇÃO PARA CARREGAR OPÇÕES DE NATUREZA (CORRIGIDA)
  const carregarOpcoesNatureza = async (centroCustoFiltro = null) => {
  try {
    console.log(`🔍 Carregando naturezas com filtro: ${centroCustoFiltro}`);
    
    const token = localStorage.getItem("authToken");
    if (!token) return;

    const tokenPayload = JSON.parse(atob(token.split(".")[1]));
    const currentUsername = tokenPayload.username || tokenPayload.id;
    const selectedYear = localStorage.getItem("selectedYear");
    const selectedFilial = localStorage.getItem("selectedFilial");

    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };

    // ✅ CONSTRUIR WHERE CLAUSE COM FILTRO DE CENTRO DE CUSTO
    let whereClause = `AND TIPO = 'Investimento' AND selectedFilial ='${selectedFilial}' AND ANO ='${selectedYear}' AND CODUSUARIO='${currentUsername}'`;

    // ✅ ADICIONAR FILTRO DE CENTRO DE CUSTO SE FORNECIDO
    if (centroCustoFiltro && centroCustoFiltro !== "" && centroCustoFiltro !== "%") {
      whereClause += ` AND CODCCUSTO = '${centroCustoFiltro}'`;
      console.log(`✅ Filtro aplicado para centro: ${centroCustoFiltro}`);
    } else {
      console.log(`📋 Carregando todas as naturezas (sem filtro de centro)`);
    }

    console.log(`🔍 WHERE clause final: ${whereClause}`);

    const response = await fetch(
      `${API_BASE_URL}/totvs-sql/sql_MAN_ORC_0006/filter`,
      {
        method: "POST",
        headers,
        body: JSON.stringify({
          WHERE: whereClause,
          ORDER_BY: "NATUREZA",
          DISTINCT: "CODTBORCAMENTO, NATUREZA",
        }),
      }
    );

    const result = await response.json();
    
    if (result.success && result.data) {
      // ✅ REMOÇÃO DE DUPLICATAS NO FRONTEND
      const naturezasUnicas = result.data.reduce((acc, item) => {
        const existe = acc.find(
          (nat) => nat.CODTBORCAMENTO === item.CODTBORCAMENTO
        );
        if (!existe) {
          acc.push(item);
        }
        return acc;
      }, []);

      console.log(`✅ Naturezas carregadas: ${naturezasUnicas.length} itens`);
      console.log(`📋 Primeiras 3 naturezas:`, naturezasUnicas.slice(0, 3));
      
      setOpcoesNatureza(naturezasUnicas);
    } else {
      console.log("⚠️ Nenhuma natureza encontrada ou erro na API");
      setOpcoesNatureza([]);
    }
  } catch (error) {
    console.error("❌ Erro ao carregar naturezas:", error);
    setOpcoesNatureza([]);
  }
};

  // 💰 FUNÇÃO PRINCIPAL PARA CARREGAR DADOS DO ORÇAMENTO (CORRIGIDA)
  const carregarDadosOrcamento = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      if (!token) return;

      const tokenPayload = JSON.parse(atob(token.split(".")[1]));
      const currentUsername = tokenPayload.username || tokenPayload.id;
      const selectedYear = localStorage.getItem("selectedYear");
      const selectedFilial = localStorage.getItem("selectedFilial");
      const PCODCOLIGADA = localStorage.getItem("PCODCOLIGADA");
      const PCODFILIAL = localStorage.getItem("PCODFILIAL");

      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };

      // ✅ CONSTRUÇÃO CORRETA DA WHERE CLAUSE
      let whereClause = `AND TIPO = 'Investimento' AND selectedFilial ='${selectedFilial}' AND ANO ='${selectedYear}' AND CODUSUARIO='${currentUsername}'  
                         AND CODCOLIGADA = '${PCODCOLIGADA}' AND CODFILIAL ='${PCODFILIAL}' `;

      // Adicionar filtros apenas se não estiverem vazios e não forem "%"
      if (
        filtros.centroCusto &&
        filtros.centroCusto !== "" &&
        filtros.centroCusto !== "%"
      ) {
        whereClause += ` AND CODCCUSTO = '${filtros.centroCusto}'`;
      }

      if (
        filtros.natureza &&
        filtros.natureza !== "" &&
        filtros.natureza !== "%"
      ) {
        whereClause += ` AND CODTBORCAMENTO = '${filtros.natureza}'`;
      }

      console.log("WHERE Clause:", whereClause); // Para debug

      const response = await fetch(
        `${API_BASE_URL}/totvs-sql/sql_MAN_ORC_0005/filter`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            PARAM: [
              {
                CODFILIAL: PCODFILIAL,
                CODCOLIGADA: PCODCOLIGADA,
                ANO: selectedYear,
              },
            ],
            WHERE: whereClause,
            ORDER_BY: "NATUREZA, CODTBORCAMENTO",
          }),
        }
      );

      const result = await response.json();
      console.log("Resultado da API:", result); // Para debug

      if (result.success && result.data) {
        // ✅ MAPEAR DADOS INCLUINDO ALERTAS DO BANCO
        const dadosMapeados = result.data.map((item) => ({
          IdOrcto: item.IDORCTO, // ✅ CORRIGIDO: Usar nomenclatura consistente
          codigo: item.CODTBORCAMENTO,
          natureza: item.NATUREZA,
          orcado: parseFloat(item.ORCADO || 0),
          realizado: parseFloat(item.REALIZADO || 0),

          resultado:
            parseFloat(item.ORCADO || 0) -
            (parseFloat(item.REALIZADO || 0) + parseFloat(item.EMPENHADO || 0)),

          economia: parseFloat(item.ECONOMIA || 0),
          empenhado: parseFloat(item.EMPENHADO || 0),
          obs: item.OBS || "",
          justificativa: item.OBS || "",
          centroCusto: item.CENTROCUSTO,
          codCCusto: item.CODCCUSTO,
          // ✅ CARREGAR CAMPOS DE ALERTA DO BANCO DE DADOS
          email25: item.ALERTA25 || "",
          email50: item.ALERTA50 || "",
          email75: item.ALERTA75 || "",
          email100: item.ALERTA100 || "",
        }));

        setData(dadosMapeados);

        // ✅ CORREÇÃO: Passar a whereClause correta para os totais
        await carregarTotaisOrcamento(whereClause);
      }
    } catch (error) {
      console.error("Erro ao carregar dados do orçamento:", error);
    } finally {
      setLoading(false);
    }
  };

  // ✅ NOVA FUNÇÃO verVersoes COMPLETA
  const verVersoes = async (natureza, idOrcto) => {
    try {
      setLoadingVersoes(true);
      setModalVersaoVisible({ natureza, idOrcto });

      const token = localStorage.getItem("authToken");
      if (!token) {
        alert("Token não encontrado");
        return;
      }

      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };

      // Buscar histórico de versões usando a query SQL fornecida
      const whereClause = `AND re.IDORCTO = '${idOrcto}'`;

      console.log("🔍 Buscando versões para ID:", idOrcto);

      const response = await fetch(
        `${API_BASE_URL}/totvs-sql/sql_MAN_ORC_0009/filter`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            WHERE: whereClause,
            ORDER_BY: "vs.RECMODIFIEDON DESC",
          }),
        }
      );

      const result = await response.json();

      if (result.success && result.data) {
        // Mapear dados das versões
        const versoesMapeadas = result.data.map((item, index) => ({
          id: index + 1,
          nome: item.NOME || "N/A",
          versao: item.VERSAO || "N/A",
          data: formatarData(item.RECMODIFIEDON),
          dataCompleta: item.RECMODIFIEDON,
          valor: item.VALOR || "",
          idOrcto: item.IDORCTO,
        }));

        setVersoesData(versoesMapeadas);
        console.log("✅ Versões carregadas:", versoesMapeadas.length);
      } else {
        console.log("⚠️ Nenhuma versão encontrada");
        setVersoesData([]);
      }
    } catch (error) {
      console.error("❌ Erro ao carregar versões:", error);
      alert(`Erro ao carregar histórico de versões: ${error.message}`);
      setVersoesData([]);
    } finally {
      setLoadingVersoes(false);
    }
  };

  // ✅ FUNÇÃO PARA FECHAR O MODAL DE VERSÕES
  const closeVersaoModal = () => {
    setModalVersaoVisible(null);
    setVersoesData([]);
    setVersaoDetalhada(null);
  };

  // ✅ FUNÇÃO PARA VER DETALHES DE UMA VERSÃO
  const verDetalhesVersao = (versao) => {
    setVersaoDetalhada(versao);
  };

  // useEffect para carregar dados iniciais
  useEffect(() => {
    carregarOpcoesCentroCusto();
    carregarOpcoesNatureza();
    atualizarTotais();
  }, [atualizarTotais]);

  // useEffect para atualizar totais quando filtros mudarem
  useEffect(() => {
    atualizarTotais();
  }, [filtros.centroCusto, filtros.natureza, atualizarTotais]);

  // useEffect para recarregar dados quando filtros mudarem
  useEffect(() => {
    if (filtros.centroCusto) {
      carregarDadosOrcamento();
    }
  }, [filtros.centroCusto, filtros.natureza]);

  // 💰 CÁLCULOS DOS TOTAIS GLOBAIS (COM FALLBACK SEGURO)
const consumoPct =
  dataTotal.length > 0 && dataTotal[0]
    ? Math.round(dataTotal[0].Consumo || 0)   // ← Manter Math.round para porcentagem
    : 0;

const OrcadoGlobal =
  dataTotal.length > 0 && dataTotal[0]
    ? parseFloat(dataTotal[0].orcado || 0)
    : 0;

const RealizadoGlobal =
  dataTotal.length > 0 && dataTotal[0]
    ? parseFloat(dataTotal[0].realizado || 0)
    : 0;

const Empenhado =
  dataTotal.length > 0 && dataTotal[0]
    ? parseFloat(dataTotal[0].empenhado || 0)
    : 0;

  // ✅ CALCULAR TOTAL GERAL DOS ITENS DE INVESTIMENTO
  const totalGeral = itensInvestimento.reduce(
    (total, item) => total + item.quantidade * item.valor,
    0
  );


const handleFiltroChange = (campo, valor) => {
  console.log(`🔧 Alterando filtro ${campo} para:`, valor);

  // ✅ ATUALIZAR FILTROS
  setFiltros((prev) => ({
    ...prev,
    [campo]: valor,
    // ✅ LIMPAR NATUREZA QUANDO MUDAR CENTRO DE CUSTO
    ...(campo === "centroCusto" && { natureza: "" }),
  }));

  // ✅ RECARREGAR NATUREZAS SE MUDOU CENTRO DE CUSTO
  if (campo === "centroCusto") {
    console.log(`📋 Recarregando naturezas para centro: ${valor}`);
    // ✅ USAR setTimeout PARA GARANTIR QUE O STATE SEJA ATUALIZADO PRIMEIRO
    setTimeout(() => {
      carregarOpcoesNatureza(valor);
    }, 100);
  }

  setCurrentPage(1);
};

  const openModal = (index) => setModalIndex(index);
  const closeModal = () => setModalIndex(null);
  const openJustModal = (index) => setModalJustIndex(index);
  const closeJustModal = () => setModalJustIndex(null);

  // ✅ FUNÇÃO ATUALIZADA PARA ABRIR MODAL DE MOVIMENTOS
  const openListModal = async (index) => {
    setModalListVisible(index);
    resetarModalMovimentos(); // cada natureza abre sem filtro e por data asc
    const item = paginatedData[index];
    const selectedYear = localStorage.getItem("selectedYear");

    // ✅ CARREGAR MOVIMENTOS COM OS PARÂMETROS CORRETOS
    await carregarMovimentos(
      item.codCCusto, // Centro de custo do item selecionado
      item.codigo, // Código da natureza orçamentária
      selectedYear // Ano selecionado
    );
  };

  const closeListModal = () => {
    setModalListVisible(null);
    setMovimentosData([]); // Limpar dados ao fechar
    resetarModalMovimentos(); // Limpar filtros e ordenação ao fechar
  };

  // ✅ FUNÇÕES PARA O MODAL DE ITENS
  const openItensModal = async (movimento) => {
    setModalItensVisible(movimento);
    
    // Pega os dados do item que está sendo visualizado na modal de movimentos
    const itemAtual = paginatedData[modalListVisible];
    
    await carregarItensMovimento(
      movimento.idMov, 
      itemAtual.codCCusto,  // centro de custo do item
      itemAtual.codigo      // código da natureza
    );
  };

  const closeItensModal = () => {
    setModalItensVisible(null);
    setItensMovimento([]); // Limpar dados ao fechar
  };

  // ✅ FUNÇÃO PARA ABRIR MODAL DE INVESTIMENTO E CARREGAR DADOS - CORRIGIDA
  const openInvestModal = async (index) => {
    // ✅ PEGAR DADOS DO ITEM SELECIONADO PRIMEIRO
    const item = paginatedData[index];
    const selectedYear = localStorage.getItem("selectedYear");

    console.log("🔍 Item selecionado para modal:", item);
    console.log("🆔 IdOrcto do item:", item.IdOrcto);

    // ✅ VERIFICAR SE O IdOrcto EXISTE
    if (!item.IdOrcto) {
      alert("❌ Erro: IdOrcto não encontrado no item selecionado!");
      console.error("❌ Item sem IdOrcto:", item);
      return;
    }

    // ✅ DEFINIR CONTEXTO E FORMULÁRIO ANTES DE ABRIR O MODAL
    const context = {
      centroCusto: item.codCCusto,
      natureza: item.codigo,
      ano: selectedYear,
      item: item,
      IdOrcto: item.IdOrcto,
    };

    // ✅ SALVAR CONTEXTO PRIMEIRO
    setModalContext(context);

    // ✅ CONFIGURAR O FORMULÁRIO COM O IdOrcto CORRETO ANTES DE ABRIR
    setNovoInvestimento({
      IdOrcto: item.IdOrcto, // ✅ DEFINIR PRIMEIRO
      codigo: "",
      descricao: "",
      quantidade: 1,
      valor: 0,
      patrimonio: false,
      justificativa: "",
      responsavel: "",
      detalhamento: "",
      objetivo: "",
      justificativa2: "",
      linkProjeto: "",
      dataPrevista: "",
      categoria: "",
      status: "Proposto",
    });

    console.log("✅ IdOrcto definido no formulário:", item.IdOrcto);

    // ✅ CARREGAR ITENS ANTES DE ABRIR O MODAL
    await carregarItensInvestimento(
      context.centroCusto,
      context.natureza,
      context.ano
    );

    // ✅ SÓ ABRIR O MODAL APÓS TUDO ESTAR CONFIGURADO
    setShowInvestModal(true);
  };

  const handleModalChange = (field, value) => {
    const globalIndex = (currentPage - 1) * itemsPerPage + modalIndex;
    const newData = [...data];
    newData[globalIndex][field] = value;
    setData(newData);
  };

  const handleJustChange = (value) => {
    const globalIndex = (currentPage - 1) * itemsPerPage + modalJustIndex;
    const newData = [...data];
    newData[globalIndex].justificativa = value;
    setData(newData);
  };


  // ✅ FUNÇÃO PARA CARREGAR DADOS DO ANO ANTERIOR
  const carregarDadosAnoAnterior = useCallback(async (item) => {
    try {
      setLoadingAnoAnterior(true);
      const token = localStorage.getItem("authToken");
      if (!token) {
        alert("Token não encontrado");
        return;
      }

      const selectedYear = localStorage.getItem("selectedYear");
      const anoAnterior = parseInt(selectedYear) - 1;
      const PCODCOLIGADA = localStorage.getItem("PCODCOLIGADA");
      const PCODFILIAL = localStorage.getItem("PCODFILIAL");

      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };

      // ✅ CONSTRUÇÃO DA WHERE CLAUSE PARA ANO ANTERIOR
      let whereClause = `AND ANO = '${anoAnterior}' AND CODCOLIGADA = '${PCODCOLIGADA}' AND CODFILIAL = '${PCODFILIAL}'`;

      // Filtrar pelo mesmo centro de custo e natureza do item atual
      if (item.codCCusto) {
        whereClause += ` AND CODCCUSTO = '${item.codCCusto}'`;
      }

      if (item.codigo) {
        whereClause += ` AND CODTBORCAMENTO = '${item.codigo}'`;
      }

      console.log("🔍 Carregando dados do ano anterior:", anoAnterior);
      console.log("🔍 WHERE clause:", whereClause);

      const response = await fetch(
        `${API_BASE_URL}/totvs-sql/sql_MAN_ORC_0005/filter`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            PARAM: [
              {
                CODFILIAL: PCODFILIAL,
                CODCOLIGADA: PCODCOLIGADA,
                ANO: anoAnterior,
              },
            ],
            WHERE: whereClause,
          }),
        }
      );

      const result = await response.json();
      console.log("📊 Resultado do ano anterior:", result);

      if (result.success && result.data && result.data.length > 0) {
        // ✅ MAPEAR DADOS DO ANO ANTERIOR
        const dadosMapeados = result.data[0]; // Pegar o primeiro registro

        const dadosFormatados = {
          ano: anoAnterior,
          natureza: dadosMapeados.NATUREZA || item.natureza,
          codigo: dadosMapeados.CODTBORCAMENTO || item.codigo,
          centroCusto: dadosMapeados.CENTROCUSTO || item.centroCusto,
          orcado: parseFloat(dadosMapeados.ORCADO || 0),
          realizado: parseFloat(dadosMapeados.REALIZADO || 0),
          economia: parseFloat(dadosMapeados.ECONOMIA || 0),
          eficiente: (
            (parseFloat(dadosMapeados.REALIZADO || 0) /
              parseFloat(dadosMapeados.ORCADO || 0)) *
            100
          ).toFixed(2),
          // Cálculos adicionais
          consumoPercent:
            dadosMapeados.ORCADO > 0
              ? Math.round(
                  (parseFloat(dadosMapeados.REALIZADO || 0) /
                    parseFloat(dadosMapeados.ORCADO || 0)) *
                    100
                )
              : 0,
          saldo:
            parseFloat(dadosMapeados.ORCADO || 0) -
            parseFloat(dadosMapeados.REALIZADO || 0),
        };

        setDadosAnoAnterior(dadosFormatados);
      } else {
        console.log("⚠️ Nenhum dado encontrado para o ano anterior");
        setDadosAnoAnterior({
          ano: anoAnterior,
          natureza: item.natureza,
          codigo: item.codigo,
          centroCusto: item.centroCusto,
          orcado: 0,
          realizado: 0,
          economia: 0,
          eficiente: 0,
          consumoPercent: 0,
          saldo: 0,
          semDados: true,
        });
      }
    } catch (error) {
      console.error("❌ Erro ao carregar dados do ano anterior:", error);
      alert(`Erro ao carregar dados do ano anterior: ${error.message}`);
      setDadosAnoAnterior(null);
    } finally {
      setLoadingAnoAnterior(false);
    }
  }, []);

  // ✅ FUNÇÃO PARA ABRIR O MODAL DO ANO ANTERIOR
  const abrirModalAnoAnterior = async (index) => {
    const item = paginatedData[index];
    setModalAnoAnteriorVisible(index);
    await carregarDadosAnoAnterior(item);
  };

  // ✅ FUNÇÃO PARA FECHAR O MODAL DO ANO ANTERIOR
  const fecharModalAnoAnterior = () => {
    setModalAnoAnteriorVisible(null);
    setDadosAnoAnterior(null);
  };

  return (
    <div className="container-fluid py-2 px-4" style={{ minHeight: "100vh" }}>
      <div
        className="bg-white rounded shadow p-4"
        style={{ backgroundColor: "#ffffffb3" }}
      >
        {/* TÍTULO */}
        <h5
          className="text-dark fw-semibold mb-4"
          style={{ fontSize: "1.50rem" }}
        >
          💼 Painel de <b className="text-success ">Investimento</b> Gerais —{" "}
          <span className="text-dark fw-semibold mb-4">
            ✅ Ano {localStorage.getItem("selectedYear")} -{" "}
            {localStorage.getItem("NomeFilial")}
            {/* ✅ INDICADOR DE STATUS DE EDIÇÃO */}
            {!podeEditarOrcamento() && (
              <span className="badge bg-warning text-dark ms-2">
                🔒 Somente Leitura
              </span>
            )}
            {podeEditarOrcamento() && (
              <span className="badge bg-succes ms-2">✏️ Editável</span>
            )}
          </span>
          {filtros.centroCusto && filtros.centroCusto !== "%" && (
            <span className="badge bg-info ms-2">
              Centro: {filtros.centroCusto}
            </span>
          )}
        </h5>
        {!podeEditarOrcamento() && (
          <div
            className="alert alert-warning d-flex align-items-center mb-3"
            role="alert"
          >
            <div className="me-2" style={{ fontSize: "1.5rem" }}>
              🔒
            </div>
            <div>
              <strong>Modo Somente Leitura</strong>
              <br />
              <small>
                Os valores orçamentários não podem ser editados para o ano{" "}
                <strong>{localStorage.getItem("selectedYear")}</strong>. Apenas
                anos futuros (2026 em diante) permitem alterações.
              </small>
            </div>
          </div>
        )}
        {/* FILTROS PRINCIPAIS */}
<div className="row g-3 mb-3">
  {/* Centro de Custo */}
  <div className="col-md-4">
    <label className="form-label fw-medium">Centro de Custo</label>
    <select
      className="form-select shadow-sm"
      value={filtros.centroCusto}
      onChange={(e) =>
        handleFiltroChange("centroCusto", e.target.value)
      }
    >
      <option value="">Selecione um Centro de Custo</option>
      {opcoesCentroCusto.map((cc, index) => (
        <option key={`${cc.CODCCUSTO}-${index}`} value={cc.CODCCUSTO}>
          {cc.CODCCUSTO} - {cc.CENTROCUSTO}
        </option>
      ))}
    </select>
  </div>

  {/* ✅ NATUREZA ORÇAMENTÁRIA CORRIGIDA */}
  <div className="col-md-4">
    <label className="form-label fw-medium">
      Natureza Orçamentária
      {/* ✅ INDICADOR VISUAL QUANDO ESTÁ FILTRADO */}
      {filtros.centroCusto && filtros.centroCusto !== "" && (
        <small className="text-success ms-2">
          (filtrado por: {filtros.centroCusto})
        </small>
      )}
    </label>

    <select
      className="form-select shadow-sm"
      value={filtros.natureza}
      onChange={(e) => handleFiltroChange("natureza", e.target.value)}
    >
      {/* ✅ OPÇÃO DINÂMICA BASEADA NO FILTRO */}
      <option value="">
        {filtros.centroCusto && filtros.centroCusto !== ""
          ? `Todas do Centro ${filtros.centroCusto}`
          : "Todas as Naturezas"}
      </option>

      {/* ✅ MAPEAR NATUREZAS (já filtradas pela função carregarOpcoesNatureza) */}
      {opcoesNatureza.map((nat, index) => (
        <option
          key={`${nat.CODTBORCAMENTO}-${index}`}
          value={nat.CODTBORCAMENTO}
        >
          {nat.CODTBORCAMENTO} - {nat.NATUREZA}
        </option>
      ))}
    </select>

    {/* ✅ FEEDBACK QUANDO NÃO HÁ NATUREZAS PARA O CENTRO */}
    {filtros.centroCusto &&
      filtros.centroCusto !== "" &&
      opcoesNatureza.length === 0 && (
        <small className="text-warning mt-1 d-block">
          ⚠️ Nenhuma natureza encontrada para este centro de custo
        </small>
      )}

    {/* ✅ CONTADOR DE NATUREZAS DISPONÍVEIS */}
    {opcoesNatureza.length > 0 && (
      <small className="text-muted mt-1 d-block">
        {opcoesNatureza.length} natureza(s) disponível(eis)
        {filtros.centroCusto &&
          filtros.centroCusto !== "" &&
          " para este centro"}
      </small>
    )}
  </div>

  {/* ✅ ADICIONAR TERCEIRA COLUNA PARA EQUILIBRAR O LAYOUT */}
  <div className="col-md-4">
    <label className="form-label fw-medium">Ações Rápidas</label>
    <div className="d-flex gap-2">
      <button 
        className="btn btn-outline-primary btn-sm flex-fill"
        onClick={() => {
          setFiltros(prev => ({ ...prev, centroCusto: "", natureza: "" }));
          carregarOpcoesNatureza(); // Recarregar todas as naturezas
        }}
      >
        🔄 Limpar Filtros
      </button>
      <button 
        className="btn btn-outline-success btn-sm flex-fill"
        onClick={carregarDadosOrcamento}
      >
        📊 Atualizar
      </button>
    </div>
  </div>
</div>

        {/* CONTROLES E RESUMOS */}
        <div className="row g-3 mb-4">
          {/* Total Geral */}
          <div className="col-md-2">
            <label className="form-label fw-medium">
              💰 Total Orçado
              <small className="text-muted">
                (
                {loadingTotais
                  ? "⏳ Carregando..."
                  : dataTotal.length > 0
                  ? "✅ Carregado"
                  : "📝 Aguardando"}
                )
              </small>
            </label>
            <input
              type="text"
              className={`form-control shadow-sm ${
                loadingTotais
                  ? "border-warning"
                  : OrcadoGlobal > 0
                  ? "border-success"
                  : "border-secondary"
              }`}
              value={
                loadingTotais
                  ? "Carregando..."
                  : OrcadoGlobal.toLocaleString("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                    })
              }
              readOnly
            />
          </div>

          {/* Empenhado */}
          <div className="col-md-2">
            <label className="form-label fw-medium text-black">
              ⚠️ Empenhado
            </label>
            <input
              type="text"
              className={`form-control shadow-sm ${
                loadingTotais
                  ? "border-warning"
                  : Empenhado > 0
                  ? "border-warning"
                  : "border-secondary"
              }`}
              value={
                loadingTotais
                  ? "Carregando..."
                  : Empenhado.toLocaleString("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                    })
              }
              readOnly
            />
          </div>

          {/* Realizado */}
          <div className="col-md-2">
            <label className="form-label fw-medium text-danger">
              📊 Realizado
            </label>
            <input
              type="text"
              className={`form-control shadow-sm ${
                loadingTotais
                  ? "border-warning"
                  : RealizadoGlobal > 0
                  ? "border-danger"
                  : "border-secondary"
              }`}
              value={
                loadingTotais
                  ? "Carregando..."
                  : RealizadoGlobal.toLocaleString("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                    })
              }
              readOnly
            />
          </div>

          {/* Consumo */}
          <div className="col-md-3">
            <label className="form-label fw-medium text-danger">
              📈 Consumo
              <small className="text-muted">({consumoPct}%)</small>
            </label>
            <div className="progress" style={{ height: "2.4rem" }}>
              {loadingTotais ? (
                <div
                  className="progress-bar bg-warning progress-bar-striped progress-bar-animated"
                  style={{ width: "100%" }}
                >
                  <span className="fw-bold text-dark">Carregando...</span>
                </div>
              ) : (
                <div
                  className={`progress-bar ${
                    consumoPct > 80
                      ? "bg-danger"
                      : consumoPct > 50
                      ? "bg-warning"
                      : "bg-success"
                  }`}
                  role="progressbar"
                  style={{ width: `${Math.min(100, consumoPct)}%` }}
                  aria-valuenow={consumoPct}
                  aria-valuemin="0"
                  aria-valuemax="100"
                >
                  {`${consumoPct}%`}
                </div>
              )}
            </div>
          </div>

          {/* Relatórios 
          
          const PpCODCOLIGADA = localStorage.getItem("PCODCOLIGADA");
          const PpCODFILIAL = localStorage.getItem("PCODFILIAL");
          const PpANO = localStorage.getItem("selectedYear");
          const PpTIPO = "Despesas";
          const PpCODUSUARIO = localStorage.getItem("username");

          */}
          <div className="col-md-3">
            <label className="form-label fw-medium text-danger">
              Relatórios
            </label>
            <div className="mt-1">
              <BotaoRelatorio
                tipo="lancamentos-analitico"
                texto="Analítico"
                icone="🆚"
                formato="pdf"
                parametros={{
                  pCODCOLIGADA: PpCODCOLIGADA, // use seus estados atuais
                  pCODFILIAL: PpCODFILIAL,
                  pANO: PpANO,
                  pTIPO: PpTIPO,
                  pCODUSUARIO: PpCODUSUARIO,
                }}
                abrirEmModal={true}
                onStartGeneration={iniciarGeracaoRelatorio}
                onFinishGeneration={finalizarGeracaoRelatorio}
              />

              <BotaoRelatorio
                tipo="lancamentos-sintetico"
                texto="Sintético"
                icone="📝"
                formato="pdf"
                parametros={{
                  pCODCOLIGADA: PpCODCOLIGADA, // use seus estados atuais
                  pCODFILIAL: PpCODFILIAL,
                  pANO: PpANO,
                  pTIPO: PpTIPO,
                  pCODUSUARIO: PpCODUSUARIO,
                }}
                abrirEmModal={true}
                onStartGeneration={iniciarGeracaoRelatorio}
                onFinishGeneration={finalizarGeracaoRelatorio}
              />
            </div>
          </div>

        </div>
        {/* LOADING */}
        {loading && (
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Carregando...</span>
            </div>
          </div>
        )}
        {/* ÁREA ROLÁVEL DA TABELA */}
        <div
          className="table-responsive rounded"
          style={{
            maxHeight: "67vh",
            overflowY: "auto",
          }}
        >
          <table className="table table-striped table-hover align-middle mb-0">
            <thead
              className="table-light text-dark"
              style={{
                position: "sticky",
                top: 0,
                zIndex: 10,
                backgroundColor: "#ffffff",
              }}
            >
              <tr>
                <th className="small" hidden>
                  Código
                </th>
                <th className="small">Natureza Orçamentária</th>
                <th className="small">Orçado (Editar)</th>
                <th className="small">Realizado</th>
                <th className="small">Empenhado</th>
                <th className="small">Saldo</th>
                <th className="small">Consumo</th>
                <th className="small">Itens</th>
                <th className="small">NF</th>
                <th className="small">Obs</th>
                <th className="small">Alerta</th>
                <th className="small">Versão</th>
                <th className="small">
                  {localStorage.getItem("selectedYear") - 1}
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((item, index) => {
                const consumoPercent =
                  item.orcado > 0
                    ? Math.min(
                        100,
                        Math.round((item.realizado / item.orcado) * 100)
                      )
                    : 0;
                return (
                  <tr key={`${item.IdOrcto}-${index}`}>
                    <td hidden>
                      <span className="text-muted small">{item.IdOrcto}</span>
                    </td>
                    <td>
                      <div>
                        <strong>{item.natureza}</strong>
                        <br />
                        <small className="text-muted">{item.codigo}</small>
                      </div>
                    </td>
                    <td>
                      <input
                        style={{
                          width: "130px",
                          padding: "2px 6px",
                          // ✅ ESTILO VISUAL PARA ANOS BLOQUEADOS
                          backgroundColor: !podeEditarOrcamento()
                            ? "#f8f9fa"
                            : "white",
                          cursor: !podeEditarOrcamento()
                            ? "not-allowed"
                            : "text",
                        }}
                        type="text"
                        className={`form-control form-control-sm ${
                          !podeEditarOrcamento() ? "text-muted" : ""
                        }`}
                        key={item.orcado}
                        defaultValue={`${item.orcado.toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        }) }`}
                        onBlur={(e) =>
                          handleOrcadoChange(index, e.target.value)
                        }
                        // ✅ DESABILITAR PARA ANOS <= 2025
                        disabled
                        // ✅ TOOLTIP EXPLICATIVO
                        title={
                          !podeEditarOrcamento()
                            ? `🔒 Edição bloqueada para o ano ${localStorage.getItem(
                                "selectedYear"
                              )}. Apenas anos futuros (2026+) podem ser editados.`
                            : "Clique para editar o valor orçado"
                        }
                        // ✅ PLACEHOLDER INFORMATIVO
                        placeholder={
                          !podeEditarOrcamento()
                            ? "Bloqueado"
                            : "Digite o valor"
                        }
                      />
                      {/* ✅ ÍCONE VISUAL PARA INDICAR BLOQUEIO */}
                      {!podeEditarOrcamento() && (
                        <div
                          className="position-absolute"
                          style={{
                            top: "50%",
                            right: "5px",
                            transform: "translateY(-50%)",
                            fontSize: "12px",
                            color: "#6c757d",
                          }}
                        >
                          
                        </div>
                      )}
                    </td>
                    <td>
                      <span className="badge bg-info text-dark ">
                        {item.realizado.toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </span>
                    </td>
                    <td>
                      <span className="badge bg-warning-subtle text-dark shadow-sm">
                        {item.empenhado.toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`badge ${
                          item.resultado >= 0
                            ? "bg-success-subtle"
                            : "bg-danger-subtle"
                        } text-dark shadow-sm`}
                      >
                        {item.resultado.toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </span>
                    </td>
                    <td style={{ minWidth: "10rem" }}>
                      <div className="progress" style={{ height: "1.2rem" }}>
                        <div
                          className={`progress-bar ${
                            consumoPercent > 80
                              ? "bg-danger"
                              : consumoPercent > 50
                              ? "bg-warning"
                              : "bg-success"
                          }`}
                          role="progressbar"
                          style={{ width: `${consumoPercent}%` }}
                          aria-valuenow={consumoPercent}
                          aria-valuemin="0"
                          aria-valuemax="100"
                        >
                          {`${consumoPercent}%`}
                        </div>
                      </div>
                    </td>
                    <td>
                      <button
                        className="btn btn-outline-dark btn-sm"
                        onClick={() => openInvestModal(index)}
                      >
                        ➕
                      </button>
                    </td>
                    <td>
                      <button
                        className="btn btn-outline-dark btn-sm"
                        onClick={() => openListModal(index)}
                      >
                        🔍
                      </button>
                    </td>
                    <td>
                      <button
                        className="btn btn-outline-dark btn-sm"
                        onClick={() => openJustModal(index)}
                      >
                        📋
                      </button>
                    </td>
                    <td>
                      <button
                        className="btn btn-outline-warning btn-sm"
                        onClick={() => openModal(index)}
                      >
                        ⚙️
                      </button>
                    </td>
                    <td>
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => verVersoes(item.natureza, item.IdOrcto)}
                      >
                        🕓
                      </button>
                    </td>
                    <td>
                      <button
                        className="btn btn-outline-info btn-sm"
                        onClick={() => abrirModalAnoAnterior(index)}
                        title="Ver dados do ano anterior"
                      >
                        📅
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {/* ✅ NOVO MODAL MODERNO DE INVESTIMENTO */}
        {showInvestModal && (
          <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0,0,0,0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "1rem",
              zIndex: 9999,
            }}
          >
            <div
              className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl max-h-[90vh] overflow-hidden"
              style={{
                backgroundColor: "white",
                borderRadius: "1rem",
                boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
                width: "100%",
                maxWidth: "80rem",
                maxHeight: "90vh",
                overflow: "hidden",
              }}
            >
              {/* Header */}
              <div
                className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6"
                style={{
                  background: "linear-gradient(to right, #2563eb, #1d4ed8)",
                  color: "white",
                  padding: "1.5rem",
                }}
              >
                <div
                  className="flex items-center justify-between"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <div>
                    <h2
                      className="text-2xl font-bold"
                      style={{
                        fontSize: "1.5rem",
                        fontWeight: "bold",
                        margin: 0,
                      }}
                    >
                      Gestão de Investimentos
                    </h2>
                    <p
                      className="text-blue-100 mt-1"
                      style={{
                        color: "rgba(219, 234, 254, 1)",
                        marginTop: "0.25rem",
                        margin: "0.25rem 0 0 0",
                      }}
                    >
                      Cadastro e aprovação de itens orçamentários
                    </p>
                  </div>
                  <div className="text-right" style={{ textAlign: "right" }}>
                    <div
                      className="text-3xl font-bold"
                      style={{ fontSize: "1.875rem", fontWeight: "bold" }}
                    >
                      {totalGeral.toLocaleString("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                      })}
                    </div>
                    <div
                      className="text-blue-200 text-sm"
                      style={{
                        color: "rgba(191, 219, 254, 1)",
                        fontSize: "0.875rem",
                      }}
                    >
                      Total Geral
                    </div>
                  </div>
                  <button
                    type="button"
                    className="text-white"
                    onClick={() => {
                      setShowInvestModal(false);
                      // Limpar dados ao fechar
                      setItensInvestimento([]);
                      setModalContext(null);
                      setNovoInvestimento({
                        idOrcto: "",
                        codigo: "",
                        descricao: "",
                        quantidade: 1,
                        valor: 0,
                        patrimonio: false,
                        justificativa: "",
                        responsavel: "",
                        detalhamento: "",
                        objetivo: "",
                        justificativa2: "",
                        linkProjeto: "",
                        dataPrevista: "",
                        categoria: "",
                        status: "Proposto",
                      });
                      setEditandoIndex(null);
                      setValidationErrors({});
                    }}
                    disabled={salvandoItens}
                    style={{
                      background: "none",
                      border: "none",
                      color: "white",
                      fontSize: "1.5rem",
                      cursor: "pointer",
                      padding: "0.5rem",
                    }}
                  >
                    ✕
                  </button>
                </div>
              </div>

              <div
                className="flex h-full max-h-[calc(90vh-140px)]"
                style={{
                  display: "flex",
                  height: "100%",
                  maxHeight: "calc(90vh - 140px)",
                }}
              >
                {/* Formulário */}
                <div
                  className="w-2/5 p-6 bg-gray-50 border-r overflow-y-auto"
                  style={{
                    width: "45%",
                    padding: "1.5rem",
                    backgroundColor: "#f9fafb",
                    borderRight: "1px solid #e5e7eb",
                    overflowY: "auto",
                  }}
                >
                  <h3
                    className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2"
                    style={{
                      fontSize: "1.125rem",
                      fontWeight: "600",
                      color: "#111827",
                      marginBottom: "1rem",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    <Plus size={20} />
                    {editandoIndex !== null ? "Editar Item" : "Novo Item"}
                  </h3>

                  <div
                    className="space-y-4"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "1rem",
                    }}
                  >
                    <div>
                      <label
                        className="block text-sm font-medium text-gray-700 mb-1"
                        style={{
                          display: "block",
                          fontSize: "0.875rem",
                          fontWeight: "500",
                          color: "#374151",
                          marginBottom: "0.25rem",
                        }}
                      >
                        Descrição *
                      </label>
                      <input
                        type="text"
                        className={`w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                          validationErrors.descricao ? "border-red-500" : ""
                        }`}
                        style={{
                          width: "100%",
                          padding: "0.75rem",
                          border: `1px solid ${
                            validationErrors.descricao ? "#ef4444" : "#d1d5db"
                          }`,
                          borderRadius: "0.5rem",
                          outline: "none",
                        }}
                        value={novoInvestimento.descricao}
                        onChange={(e) => {
                          setNovoInvestimento({
                            ...novoInvestimento,
                            descricao: e.target.value,
                          });
                          if (validationErrors.descricao) {
                            setValidationErrors({
                              ...validationErrors,
                              descricao: undefined,
                            });
                          }
                        }}
                        placeholder="Descreva o item de investimento"
                      />
                      {validationErrors.descricao && (
                        <div
                          className="text-red-500 text-sm mt-1"
                          style={{
                            color: "#ef4444",
                            fontSize: "0.875rem",
                            marginTop: "0.25rem",
                          }}
                        >
                          {validationErrors.descricao}
                        </div>
                      )}
                    </div>

                    <div
                      className="grid grid-cols-2 gap-3"
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "0.75rem",
                      }}
                    >
                      <div>
                        <label
                          className="block text-sm font-medium text-gray-700 mb-1"
                          style={{
                            display: "block",
                            fontSize: "0.875rem",
                            fontWeight: "500",
                            color: "#374151",
                            marginBottom: "0.25rem",
                          }}
                        >
                          Quantidade *
                        </label>
                        <input
                          type="number"
                          min="1"
                          className={`w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                            validationErrors.quantidade ? "border-red-500" : ""
                          }`}
                          style={{
                            width: "100%",
                            padding: "0.75rem",
                            border: `1px solid ${
                              validationErrors.quantidade
                                ? "#ef4444"
                                : "#d1d5db"
                            }`,
                            borderRadius: "0.5rem",
                            outline: "none",
                          }}
                          value={novoInvestimento.quantidade}
                          onChange={(e) => {
                            setNovoInvestimento({
                              ...novoInvestimento,
                              quantidade: Number(e.target.value),
                            });
                            if (validationErrors.quantidade) {
                              setValidationErrors({
                                ...validationErrors,
                                quantidade: undefined,
                              });
                            }
                          }}
                        />
                        {validationErrors.quantidade && (
                          <div
                            className="text-red-500 text-sm mt-1"
                            style={{
                              color: "#ef4444",
                              fontSize: "0.875rem",
                              marginTop: "0.25rem",
                            }}
                          >
                            {validationErrors.quantidade}
                          </div>
                        )}
                      </div>
                      <div>
                        <label
                          className="block text-sm font-medium text-gray-700 mb-1"
                          style={{
                            display: "block",
                            fontSize: "0.875rem",
                            fontWeight: "500",
                            color: "#374151",
                            marginBottom: "0.25rem",
                          }}
                        >
                          Valor Unitário *
                        </label>
                        <div
                          className="relative"
                          style={{ position: "relative" }}
                        >
                          <DollarSign
                            className="absolute left-3 top-3.5 text-gray-400"
                            size={16}
                            style={{
                              position: "absolute",
                              left: "0.75rem",
                              top: "0.875rem",
                              color: "#9ca3af",
                            }}
                          />
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            className={`w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                              validationErrors.valor ? "border-red-500" : ""
                            }`}
                            style={{
                              width: "100%",
                              paddingLeft: "2.5rem",
                              paddingRight: "0.75rem",
                              paddingTop: "0.75rem",
                              paddingBottom: "0.75rem",
                              border: `1px solid ${
                                validationErrors.valor ? "#ef4444" : "#d1d5db"
                              }`,
                              borderRadius: "0.5rem",
                              outline: "none",
                            }}
                            value={novoInvestimento.valor}
                            onChange={(e) => {
                              setNovoInvestimento({
                                ...novoInvestimento,
                                valor: Number(e.target.value),
                              });
                              if (validationErrors.valor) {
                                setValidationErrors({
                                  ...validationErrors,
                                  valor: undefined,
                                });
                              }
                            }}
                          />
                        </div>
                        {validationErrors.valor && (
                          <div
                            className="text-red-500 text-sm mt-1"
                            style={{
                              color: "#ef4444",
                              fontSize: "0.875rem",
                              marginTop: "0.25rem",
                            }}
                          >
                            {validationErrors.valor}
                          </div>
                        )}
                      </div>
                    </div>

                    <div
                      className="grid grid-cols-2 gap-3"
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "0.75rem",
                      }}
                    >
                      <div>
                        <label
                          className="block text-sm font-medium text-gray-700 mb-1"
                          style={{
                            display: "block",
                            fontSize: "0.875rem",
                            fontWeight: "500",
                            color: "#374151",
                            marginBottom: "0.25rem",
                          }}
                        >
                          Categoria *
                        </label>
                        <div
                          className="relative"
                          style={{ position: "relative" }}
                        >
                          <Package
                            className="absolute left-3 top-3.5 text-gray-400"
                            size={16}
                            style={{
                              position: "absolute",
                              left: "0.75rem",
                              top: "0.875rem",
                              color: "#9ca3af",
                            }}
                          />
                          <input
                            type="text"
                            className={`w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                              validationErrors.categoria ? "border-red-500" : ""
                            }`}
                            style={{
                              width: "100%",
                              paddingLeft: "2.5rem",
                              paddingRight: "0.75rem",
                              paddingTop: "0.75rem",
                              paddingBottom: "0.75rem",
                              border: `1px solid ${
                                validationErrors.categoria
                                  ? "#ef4444"
                                  : "#d1d5db"
                              }`,
                              borderRadius: "0.5rem",
                              outline: "none",
                            }}
                            value={novoInvestimento.categoria}
                            onChange={(e) => {
                              setNovoInvestimento({
                                ...novoInvestimento,
                                categoria: e.target.value,
                              });
                              if (validationErrors.categoria) {
                                setValidationErrors({
                                  ...validationErrors,
                                  categoria: undefined,
                                });
                              }
                            }}
                            placeholder="Ex: TI, Móveis, Software"
                          />
                        </div>
                        {validationErrors.categoria && (
                          <div
                            className="text-red-500 text-sm mt-1"
                            style={{
                              color: "#ef4444",
                              fontSize: "0.875rem",
                              marginTop: "0.25rem",
                            }}
                          >
                            {validationErrors.categoria}
                          </div>
                        )}
                      </div>
                      <div>
                        <label
                          className="block text-sm font-medium text-gray-700 mb-1"
                          style={{
                            display: "block",
                            fontSize: "0.875rem",
                            fontWeight: "500",
                            color: "#374151",
                            marginBottom: "0.25rem",
                          }}
                        >
                          Status
                        </label>
                        <select
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          style={{
                            width: "100%",
                            padding: "0.75rem",
                            border: "1px solid #d1d5db",
                            borderRadius: "0.5rem",
                            outline: "none",
                          }}
                          value={novoInvestimento.status}
                          onChange={(e) =>
                            setNovoInvestimento({
                              ...novoInvestimento,
                              status: e.target.value,
                            })
                          }
                        >
                          <option value="Proposto">Proposto</option>
                          <option value="Em Análise">Em Análise</option>
                          <option value="Aprovado">Aprovado</option>
                          <option value="Reprovado">Reprovado</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label
                        className="block text-sm font-medium text-gray-700 mb-1"
                        style={{
                          display: "block",
                          fontSize: "0.875rem",
                          fontWeight: "500",
                          color: "#374151",
                          marginBottom: "0.25rem",
                        }}
                      >
                        Responsável *
                      </label>
                      <div
                        className="relative"
                        style={{ position: "relative" }}
                      >
                        <User
                          className="absolute left-3 top-3.5 text-gray-400"
                          size={16}
                          style={{
                            position: "absolute",
                            left: "0.75rem",
                            top: "0.875rem",
                            color: "#9ca3af",
                          }}
                        />
                        <input
                          type="text"
                          className={`w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                            validationErrors.responsavel ? "border-red-500" : ""
                          }`}
                          style={{
                            width: "100%",
                            paddingLeft: "2.5rem",
                            paddingRight: "0.75rem",
                            paddingTop: "0.75rem",
                            paddingBottom: "0.75rem",
                            border: `1px solid ${
                              validationErrors.responsavel
                                ? "#ef4444"
                                : "#d1d5db"
                            }`,
                            borderRadius: "0.5rem",
                            outline: "none",
                          }}
                          value={novoInvestimento.responsavel}
                          onChange={(e) => {
                            setNovoInvestimento({
                              ...novoInvestimento,
                              responsavel: e.target.value,
                            });
                            if (validationErrors.responsavel) {
                              setValidationErrors({
                                ...validationErrors,
                                responsavel: undefined,
                              });
                            }
                          }}
                          placeholder="Nome do responsável"
                        />
                      </div>
                      {validationErrors.responsavel && (
                        <div
                          className="text-red-500 text-sm mt-1"
                          style={{
                            color: "#ef4444",
                            fontSize: "0.875rem",
                            marginTop: "0.25rem",
                          }}
                        >
                          {validationErrors.responsavel}
                        </div>
                      )}
                    </div>

                    <div>
                      <label
                        className="block text-sm font-medium text-gray-700 mb-1"
                        style={{
                          display: "block",
                          fontSize: "0.875rem",
                          fontWeight: "500",
                          color: "#374151",
                          marginBottom: "0.25rem",
                        }}
                      >
                        Data Prevista
                      </label>
                      <div
                        className="relative"
                        style={{ position: "relative" }}
                      >
                        <Calendar
                          className="absolute left-3 top-3.5 text-gray-400"
                          size={16}
                          style={{
                            position: "absolute",
                            left: "0.75rem",
                            top: "0.875rem",
                            color: "#9ca3af",
                          }}
                        />
                        <input
                          type="date"
                          className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          style={{
                            width: "100%",
                            paddingLeft: "2.5rem",
                            paddingRight: "0.75rem",
                            paddingTop: "0.75rem",
                            paddingBottom: "0.75rem",
                            border: "1px solid #d1d5db",
                            borderRadius: "0.5rem",
                            outline: "none",
                          }}
                          value={novoInvestimento.dataPrevista}
                          onChange={(e) =>
                            setNovoInvestimento({
                              ...novoInvestimento,
                              dataPrevista: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>

                    <div
                      className="flex items-center space-x-3"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.75rem",
                      }}
                    >
                      <input
                        type="checkbox"
                        id="patrimonio"
                        checked={novoInvestimento.patrimonio}
                        onChange={(e) =>
                          setNovoInvestimento({
                            ...novoInvestimento,
                            patrimonio: e.target.checked,
                          })
                        }
                        className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                        style={{ width: "1rem", height: "1rem" }}
                      />
                      <label
                        htmlFor="patrimonio"
                        className="text-sm font-medium text-gray-700"
                        style={{
                          fontSize: "0.875rem",
                          fontWeight: "500",
                          color: "#374151",
                        }}
                      >
                        Este item será patrimônio da empresa
                      </label>
                    </div>

                    <div>
                      <label
                        className="block text-sm font-medium text-gray-700 mb-1"
                        style={{
                          display: "block",
                          fontSize: "0.875rem",
                          fontWeight: "500",
                          color: "#374151",
                          marginBottom: "0.25rem",
                        }}
                      >
                        DESCRIÇÃO DETALHADA DO INVESTIMENTO
                      </label>

                      <div
                        className="relative"
                        style={{ position: "relative" }}
                      >
                        <Target
                          className="absolute left-3 top-3 text-gray-400"
                          size={16}
                          style={{
                            position: "absolute",
                            left: "0.75rem",
                            top: "0.75rem",
                            color: "#9ca3af",
                          }}
                        />
                        <textarea
                          className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          style={{
                            width: "100%",
                            paddingLeft: "2.5rem",
                            paddingRight: "0.75rem",
                            paddingTop: "0.75rem",
                            paddingBottom: "0.75rem",
                            border: "1px solid #d1d5db",
                            borderRadius: "0.5rem",
                            outline: "none",
                            minHeight: "80px",
                            resize: "vertical",
                          }}
                          rows="2"
                          value={novoInvestimento.detalhamento}
                          onChange={(e) =>
                            setNovoInvestimento({
                              ...novoInvestimento,
                              detalhamento: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>

                    <div>
                      <label
                        className="block text-sm font-medium text-gray-700 mb-1"
                        style={{
                          display: "block",
                          fontSize: "0.875rem",
                          fontWeight: "500",
                          color: "#374151",
                          marginBottom: "0.25rem",
                        }}
                      >
                        OBJETIVO DO INVESTIMENTO
                      </label>

                      <div
                        className="relative"
                        style={{ position: "relative" }}
                      >
                        <Target
                          className="absolute left-3 top-3 text-gray-400"
                          size={16}
                          style={{
                            position: "absolute",
                            left: "0.75rem",
                            top: "0.75rem",
                            color: "#9ca3af",
                          }}
                        />
                        <textarea
                          className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          style={{
                            width: "100%",
                            paddingLeft: "2.5rem",
                            paddingRight: "0.75rem",
                            paddingTop: "0.75rem",
                            paddingBottom: "0.75rem",
                            border: "1px solid #d1d5db",
                            borderRadius: "0.5rem",
                            outline: "none",
                            minHeight: "80px",
                            resize: "vertical",
                          }}
                          rows="2"
                          value={novoInvestimento.objetivo}
                          onChange={(e) =>
                            setNovoInvestimento({
                              ...novoInvestimento,
                              objetivo: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>

                    <div>
                      <label
                        className="block text-sm font-medium text-gray-700 mb-1"
                        style={{
                          display: "block",
                          fontSize: "0.875rem",
                          fontWeight: "500",
                          color: "#374151",
                          marginBottom: "0.25rem",
                        }}
                      >
                        JUSTIFICATIVA
                      </label>

                      <div
                        className="relative"
                        style={{ position: "relative" }}
                      >
                        <Target
                          className="absolute left-3 top-3 text-gray-400"
                          size={16}
                          style={{
                            position: "absolute",
                            left: "0.75rem",
                            top: "0.75rem",
                            color: "#9ca3af",
                          }}
                        />
                        <textarea
                          className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          style={{
                            width: "100%",
                            paddingLeft: "2.5rem",
                            paddingRight: "0.75rem",
                            paddingTop: "0.75rem",
                            paddingBottom: "0.75rem",
                            border: "1px solid #d1d5db",
                            borderRadius: "0.5rem",
                            outline: "none",
                            minHeight: "80px",
                            resize: "vertical",
                          }}
                          rows="2"
                          value={novoInvestimento.justificativa2}
                          onChange={(e) =>
                            setNovoInvestimento({
                              ...novoInvestimento,
                              justificativa2: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>

                      <button
                        onClick={adicionarItem}
                        className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center justify-center gap-2"
                        style={{
                          width: "100%",
                          // ✅ MUDANÇA: Cor cinza quando bloqueado
                          backgroundColor: !podeEditarOrcamento() ? "#6b7280" : "#2563eb",
                          color: "white",
                          padding: "0.75rem 1rem",
                          borderRadius: "0.5rem",
                          border: "none",
                          fontWeight: "500",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "0.5rem",
                          // ✅ MUDANÇA: Cursor not-allowed quando bloqueado
                          cursor: !podeEditarOrcamento() || salvandoItens ? "not-allowed" : "pointer",
                          // ✅ MUDANÇA: Opacity reduzida quando bloqueado
                          opacity: !podeEditarOrcamento() || salvandoItens ? 0.6 : 1,
                        }}
                        // ✅ MUDANÇA: Adicionar validação podeEditarOrcamento()
                        disabled={salvandoItens || !podeEditarOrcamento()}
                        // ✅ MUDANÇA: Tooltip explicativo
                        title={
                          !podeEditarOrcamento()
                            ? `🔒 Edição bloqueada para o ano ${localStorage.getItem("selectedYear")}. Apenas anos futuros (${localStorage.getItem("selectedTrava")}+) podem ser editados.`
                            : editandoIndex !== null
                            ? "Atualizar item na lista"
                            : "Adicionar novo item à lista"
                        }
                      >
                        {/* ✅ MUDANÇA: Mostrar ícone de cadeado quando bloqueado */}
                        {!podeEditarOrcamento() ? (
                          <>
                            🔒 Bloqueado
                          </>
                        ) : editandoIndex !== null ? (
                          <>
                            <Edit2 size={16} />
                            Atualizar Item
                          </>
                        ) : (
                          <>
                            <Plus size={16} />
                            Adicionar Item
                          </>
                        )}
                      </button>
                  </div>
                </div>

                {/* Lista de Itens */}
                <div
                  className="flex-1 p-6 overflow-y-auto"
                  style={{ flex: 1, padding: "1.5rem", overflowY: "auto" }}
                >
                  <div
                    className="flex items-center justify-between mb-4"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: "1rem",
                    }}
                  >
                    <h3
                      className="text-lg font-semibold text-gray-900"
                      style={{
                        fontSize: "1.125rem",
                        fontWeight: "700",
                        color: "#111827",
                        margin: 0,
                      }}
                    >
                      Itens Cadastrados ({itensInvestimento.length})
                    </h3>
                    <div
                      className="flex gap-2"
                      style={{ display: "flex", gap: "0.5rem" }}
                    >
                      <button
                        onClick={() => setViewMode("cards")}
                        className={`px-3 py-1 rounded text-sm ${
                          viewMode === "cards"
                            ? "bg-blue-100 text-blue-700"
                            : "text-gray-600"
                        }`}
                        style={{
                          padding: "0.25rem 0.75rem",
                          borderRadius: "0.25rem",
                          fontSize: "0.875rem",
                          border: "none",
                          cursor: "pointer",
                          backgroundColor:
                            viewMode === "cards" ? "#dbeafe" : "transparent",
                          color: viewMode === "cards" ? "#1d4ed8" : "#4b5563",
                        }}
                      >
                        Cards
                      </button>
                      <button
                        onClick={() => setViewMode("table")}
                        className={`px-3 py-1 rounded text-sm ${
                          viewMode === "table"
                            ? "bg-blue-100 text-blue-700"
                            : "text-gray-600"
                        }`}
                        style={{
                          padding: "0.25rem 0.75rem",
                          borderRadius: "0.25rem",
                          fontSize: "0.875rem",
                          border: "none",
                          cursor: "pointer",
                          backgroundColor:
                            viewMode === "table" ? "#dbeafe" : "transparent",
                          color: viewMode === "table" ? "#1d4ed8" : "#4b5563",
                        }}
                      >
                        Tabela
                      </button>
                    </div>
                  </div>

                  {loadingItensInvestimento ? (
                    <div
                      className="text-center py-12"
                      style={{ textAlign: "center", padding: "3rem 0" }}
                    >
                      <div
                        className="spinner-border text-primary mb-3"
                        role="status"
                        style={{ marginBottom: "1rem" }}
                      >
                        <span className="visually-hidden">Carregando...</span>
                      </div>
                      <h3
                        className="text-lg font-medium text-gray-900 mb-2"
                        style={{
                          fontSize: "1.125rem",
                          fontWeight: "700",
                          color: "#111827",
                          marginBottom: "0.5rem",
                        }}
                      >
                        Carregando itens de investimento...
                      </h3>
                      <p className="text-gray-500" style={{ color: "#6b7280" }}>
                        Aguarde enquanto buscamos os dados no banco
                      </p>
                    </div>
                  ) : viewMode === "cards" ? (
                    <div
                      className="space-y-4"
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "1rem",
                      }}
                    >
                      {itensInvestimento.map((item, idx) => (
                        <div
                          key={idx}
                          className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow"
                          style={{
                            backgroundColor: "white",
                            borderRadius: "0.75rem",
                            border: "1px solid #e5e7eb",
                            padding: "1.25rem",
                            boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
                            transition: "box-shadow 0.15s ease-in-out",
                          }}
                        >
                          <div
                            className="flex items-start justify-between mb-3"
                            style={{
                              display: "flex",
                              alignItems: "flex-start",
                              justifyContent: "space-between",
                              marginBottom: "0.75rem",
                            }}
                          >
                            <div className="flex-1" style={{ flex: 1 }}>
                              <div
                                className="flex items-center gap-3 mb-2"
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "0.75rem",
                                  marginBottom: "0.5rem",
                                }}
                              >
                                <h4
                                  className="font-semibold text-gray-900"
                                  style={{
                                    fontWeight: "600",
                                    color: "#111827",
                                    margin: 0,
                                  }}
                                >
                                  {item.descricao}
                                </h4>
                                <StatusBadge status={item.status} />
                                {item.dataInclusao ? (
                                  <span
                                    className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200"
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: "0.25rem",
                                      padding: "0.25rem 0.5rem",
                                      borderRadius: "9999px",
                                      fontSize: "0.75rem",
                                      fontWeight: "500",
                                      backgroundColor: "#f0fdf4",
                                      color: "#15803d",
                                      border: "1px solid #bbf7d0",
                                    }}
                                    title={`Salvo em: ${formatarData(
                                      item.dataInclusao
                                    )}`}
                                  >
                                    💾 Salvo
                                  </span>
                                ) : (
                                  <span
                                    className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-orange-50 text-orange-700 border border-orange-200"
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: "0.25rem",
                                      padding: "0.25rem 0.5rem",
                                      borderRadius: "9999px",
                                      fontSize: "0.75rem",
                                      fontWeight: "500",
                                      backgroundColor: "#fff7ed",
                                      color: "#c2410c",
                                      border: "1px solid #fed7aa",
                                    }}
                                  >
                                    ✏️ Novo
                                  </span>
                                )}
                              </div>
                              <p
                                className="text-sm text-gray-600 mb-2"
                                style={{
                                  fontSize: "0.875rem",
                                  color: "#4b5563",
                                  marginBottom: "0.5rem",
                                  margin: "0 0 0.5rem 0",
                                }}
                              >
                                {item.objetivo}
                              </p>
                              <div
                                className="flex items-center gap-4 text-sm text-gray-500"
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "1rem",
                                  fontSize: "0.875rem",
                                  color: "#6b7280",
                                }}
                              >
                                <span
                                  className="flex items-center gap-1"
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "0.25rem",
                                  }}
                                >
                                  <Package size={14} />
                                  {item.categoria}
                                </span>
                                <span
                                  className="flex items-center gap-1"
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "0.25rem",
                                  }}
                                >
                                  <User size={14} />
                                  {item.responsavel}
                                </span>
                                {item.dataPrevista && (
                                  <span
                                    className="flex items-center gap-1"
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "0.25rem",
                                    }}
                                  >
                                    <Calendar size={14} />
                                    {new Date(
                                      item.dataPrevista
                                    ).toLocaleDateString("pt-BR")}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div
                              className="text-right ml-4"
                              style={{ textAlign: "right", marginLeft: "1rem" }}
                            >
                              <div
                                className="text-lg font-bold text-gray-900"
                                style={{
                                  fontSize: "1.125rem",
                                  fontWeight: "bold",
                                  color: "#111827",
                                }}
                              >
                                {(item.quantidade * item.valor).toLocaleString(
                                  "pt-BR",
                                  { style: "currency", currency: "BRL" }
                                )}
                              </div>
                              <div
                                className="text-sm text-gray-500"
                                style={{
                                  fontSize: "0.875rem",
                                  color: "#6b7280",
                                }}
                              >
                                {item.quantidade}x{" "}
                                {item.valor.toLocaleString("pt-BR", {
                                  style: "currency",
                                  currency: "BRL",
                                })}
                              </div>
                              <div
                                className="flex gap-1 mt-2"
                                style={{
                                  display: "flex",
                                  gap: "0.25rem",
                                  marginTop: "0.5rem",
                                }}
                              >
                                <button
                                  onClick={() => editarItem(idx)}
                                  className="p-1.5 text-orange-600 hover:bg-orange-50 rounded"
                                  style={{
                                    padding: "0.375rem",
                                    color: "#ea580c",
                                    backgroundColor: "transparent",
                                    border: "none",
                                    borderRadius: "0.25rem",
                                    cursor: "pointer",
                                  }}
                                  disabled={salvandoItens}
                                >
                                  <Edit2 size={14} />
                                </button>
                                <button
                                  onClick={() =>
                                    excluirItemInvestimento(item, idx)
                                  }
                                  className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                                  style={{
                                    padding: "0.375rem",
                                    color: "#dc2626",
                                    backgroundColor: "transparent",
                                    border: "none",
                                    borderRadius: "0.25rem",
                                    cursor: "pointer",
                                  }}
                                  disabled={salvandoItens}
                                  title={
                                    item.dataInclusao
                                      ? "Excluir do banco de dados"
                                      : "Remover da lista"
                                  }
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div
                      className="bg-white rounded-xl border border-gray-200 overflow-hidden"
                      style={{
                        backgroundColor: "white",
                        borderRadius: "0.75rem",
                        border: "1px solid #e5e7eb",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        className="overflow-x-auto"
                        style={{ overflowX: "auto" }}
                      >
                        <table className="w-full" style={{ width: "100%" }}>
                          <thead
                            className="bg-gray-50"
                            style={{ backgroundColor: "#f9fafb" }}
                          >
                            <tr>
                              <th
                                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase"
                                style={{
                                  padding: "0.75rem 1rem",
                                  textAlign: "left",
                                  fontSize: "0.75rem",
                                  fontWeight: "500",
                                  color: "#6b7280",
                                  textTransform: "uppercase",
                                }}
                              >
                                Item
                              </th>
                              <th
                                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase"
                                style={{
                                  padding: "0.75rem 1rem",
                                  textAlign: "left",
                                  fontSize: "0.75rem",
                                  fontWeight: "500",
                                  color: "#6b7280",
                                  textTransform: "uppercase",
                                }}
                              >
                                Qtd
                              </th>
                              <th
                                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase"
                                style={{
                                  padding: "0.75rem 1rem",
                                  textAlign: "left",
                                  fontSize: "0.75rem",
                                  fontWeight: "500",
                                  color: "#6b7280",
                                  textTransform: "uppercase",
                                }}
                              >
                                Valor Unit.
                              </th>
                              <th
                                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase"
                                style={{
                                  padding: "0.75rem 1rem",
                                  textAlign: "left",
                                  fontSize: "0.75rem",
                                  fontWeight: "500",
                                  color: "#6b7280",
                                  textTransform: "uppercase",
                                }}
                              >
                                Total
                              </th>
                              <th
                                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase"
                                style={{
                                  padding: "0.75rem 1rem",
                                  textAlign: "left",
                                  fontSize: "0.75rem",
                                  fontWeight: "500",
                                  color: "#6b7280",
                                  textTransform: "uppercase",
                                }}
                              >
                                Ações
                              </th>
                            </tr>
                          </thead>
                          <tbody
                            className="divide-y divide-gray-200"
                            style={{ borderTop: "1px solid #e5e7eb" }}
                          >
                            {itensInvestimento.map((item, idx) => (
                              <tr
                                key={idx}
                                className="hover:bg-gray-50"
                                style={{
                                  borderTop:
                                    idx > 0 ? "1px solid #e5e7eb" : "none",
                                }}
                              >
                                <td
                                  className="px-4 py-3"
                                  style={{ padding: "0.75rem 1rem" }}
                                >
                                  <div>
                                    <div
                                      className="font-medium text-gray-900"
                                      style={{
                                        fontWeight: "500",
                                        color: "#111827",
                                      }}
                                    >
                                      {item.descricao}
                                    </div>
                                    <div
                                      className="text-sm text-gray-500"
                                      style={{
                                        fontSize: "0.875rem",
                                        color: "#6b7280",
                                      }}
                                    >
                                      {item.responsavel}
                                    </div>
                                  </div>
                                </td>
                                <td
                                  className="px-4 py-3 text-sm text-gray-900"
                                  style={{
                                    padding: "0.75rem 1rem",
                                    fontSize: "0.875rem",
                                    color: "#111827",
                                  }}
                                >
                                  {item.quantidade}
                                </td>
                                <td
                                  className="px-4 py-3 text-sm text-gray-900"
                                  style={{
                                    padding: "0.75rem 1rem",
                                    fontSize: "0.875rem",
                                    color: "#111827",
                                  }}
                                >
                                  {item.valor.toLocaleString("pt-BR", {
                                    style: "currency",
                                    currency: "BRL",
                                  })}
                                </td>
                                <td
                                  className="px-4 py-3 text-sm font-medium text-gray-900"
                                  style={{
                                    padding: "0.75rem 1rem",
                                    fontSize: "0.875rem",
                                    fontWeight: "500",
                                    color: "#111827",
                                  }}
                                >
                                  {(
                                    item.quantidade * item.valor
                                  ).toLocaleString("pt-BR", {
                                    style: "currency",
                                    currency: "BRL",
                                  })}
                                </td>
                                <td
                                  className="px-4 py-3"
                                  style={{ padding: "0.75rem 1rem" }}
                                >
                                  <div
                                    className="flex gap-1"
                                    style={{ display: "flex", gap: "0.25rem" }}
                                  >
                                    <button
                                      onClick={() => editarItem(idx)}
                                      className="p-1.5 text-orange-600 hover:bg-orange-50 rounded"
                                      style={{
                                        padding: "0.375rem",
                                        color: "#ea580c",
                                        backgroundColor: "transparent",
                                        border: "none",
                                        borderRadius: "0.25rem",
                                        cursor: "pointer",
                                      }}
                                      disabled={salvandoItens}
                                    >
                                      <Edit2 size={14} />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        excluirItemInvestimento(item, idx)
                                      }
                                      disabled={salvandoItens}
                                      title={
                                        item.dataInclusao
                                          ? "Excluir do banco de dados"
                                          : "Remover da lista"
                                      }
                                      className={`
                                          p-1.5 
                                          rounded 
                                          transition-colors 
                                          ${
                                            item.dataInclusao
                                              ? "text-red-600 hover:bg-red-50"
                                              : "text-gray-400 hover:bg-gray-100"
                                          } 
                                          ${
                                            salvandoItens
                                              ? "cursor-not-allowed opacity-50"
                                              : "cursor-pointer"
                                          } 
                                          focus:outline-none focus:ring-2 focus:ring-red-300
                                        `}
                                    >
                                      <Trash2 size={14} />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {!loadingItensInvestimento &&
                    itensInvestimento.length === 0 && (
                      <div
                        className="text-center py-12"
                        style={{ textAlign: "center", padding: "3rem 0" }}
                      >
                        <Package
                          size={48}
                          className="mx-auto text-gray-400 mb-4"
                          style={{
                            margin: "0 auto 1rem auto",
                            color: "#9ca3af",
                          }}
                        />
                        <h3
                          className="text-lg font-medium text-gray-900 mb-2"
                          style={{
                            fontSize: "1.125rem",
                            fontWeight: "500",
                            color: "#111827",
                            marginBottom: "0.5rem",
                          }}
                        >
                          Nenhum item adicionado
                        </h3>
                        <p
                          className="text-gray-500"
                          style={{ color: "#6b7280" }}
                        >
                          Comece adicionando itens usando o formulário ao lado
                        </p>
                      </div>
                    )}

                  {/* Footer */}
                  <div
                    className="border-t bg-gray-50 px-6 py-4 flex items-center justify-between"
                    style={{
                      borderTop: "1px solid #e5e7eb",
                      backgroundColor: "#f9fafb",
                      padding: "1rem 1.5rem",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <div
                      className="text-sm text-gray-600"
                      style={{ fontSize: "0.875rem", color: "#4b5563" }}
                    >
                      {itensInvestimento.length} itens •
                      {
                        itensInvestimento.filter((item) => !item.dataInclusao)
                          .length
                      }{" "}
                      novos •
                      {
                        itensInvestimento.filter((item) => item.dataInclusao)
                          .length
                      }{" "}
                      salvos • Total:{" "}
                      {totalGeral.toLocaleString("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                      })}
                    </div>
                    <div
                      className="flex gap-3"
                      style={{ display: "flex", gap: "0.75rem" }}
                    >
                    <button
                      className={`
                        px-6 py-2 
                        bg-blue-600 text-white 
                        rounded-lg hover:bg-blue-700 
                        transition-colors font-medium 
                        ${
                          // ✅ MUDANÇA: Adicionar !podeEditarOrcamento() na condição
                          salvandoItens || !podeEditarOrcamento() ? "opacity-50 cursor-not-allowed" : ""
                        }
                      `}
                      style={{
                        padding: "0.5rem 1.5rem",
                        // ✅ MUDANÇA: Cor cinza quando bloqueado
                        backgroundColor: !podeEditarOrcamento() ? "#6b7280" : "#2563eb",
                        color: "white",
                        borderRadius: "0.5rem",
                        border: "none",
                        fontWeight: "500",
                        // ✅ MUDANÇA: Cursor not-allowed quando bloqueado
                        cursor: salvandoItens || !podeEditarOrcamento() ? "not-allowed" : "pointer",
                        // ✅ MUDANÇA: Opacity reduzida quando bloqueado
                        opacity: salvandoItens || !podeEditarOrcamento() ? 0.5 : 1,
                      }}
                      onClick={salvarTodosItens}
                      disabled={
                        salvandoItens ||
                        // ✅ MUDANÇA: Adicionar validação podeEditarOrcamento()
                        !podeEditarOrcamento() ||
                        (countNovos === 0 && countModificados === 0)
                      }
                      // ✅ MUDANÇA: Tooltip explicativo
                      title={
                        !podeEditarOrcamento()
                          ? `🔒 Edição bloqueada para o ano ${localStorage.getItem("selectedYear")}. Apenas anos futuros (${localStorage.getItem("selectedTrava")}+) podem ser editados.`
                          : "Salvar todos os itens novos e modificados"
                      }
                    >
                      {/* ✅ MUDANÇA: Mostrar mensagem de bloqueio quando necessário */}
                      {!podeEditarOrcamento() ? (
                        "🔒 Salvamento Bloqueado"
                      ) : salvandoItens ? (
                        <>
                          <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                          Salvando...
                        </>
                      ) : (
                        `Salvar (${countNovos} novos, ${countModificados} atualizados)`
                      )}
                    </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* MODAIS ORIGINAIS */}
        {/* ✅ MODAL DE ALERTA ATUALIZADO COM BOTÃO SALVAR */}
        {modalIndex !== null && (
          <div
            className="modal d-block"
            tabIndex="-1"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">🔔 Alerta de Consumo</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeModal}
                  ></button>
                </div>
                <div className="modal-body">
                  {[25, 50, 75, 100].map((percent) => (
                    <div className="mb-3" key={percent}>
                      <label className="form-label">
                        Email para {percent}%
                      </label>
                      <input
                        type="email"
                        className="form-control"
                        value={
                          paginatedData[modalIndex]?.[`email${percent}`] || ""
                        }
                        onChange={(e) =>
                          handleModalChange(`email${percent}`, e.target.value)
                        }
                        disabled={salvandoAlerta}
                        placeholder={`Digite o email para alerta em ${percent}%`}
                      />
                    </div>
                  ))}
                  {salvandoAlerta && (
                    <div className="text-center mt-2">
                      <div
                        className="spinner-border spinner-border-sm text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Salvando...</span>
                      </div>
                      <small className="text-muted ms-2">
                        Salvando alertas...
                      </small>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeModal}
                    disabled={salvandoAlerta}
                  >
                    Fechar
                  </button>
                  <button
                    type="button"
                    className={`btn btn-primary ${
                      salvandoAlerta ? "disabled" : ""
                    }`}
                    onClick={salvarAlerta}
                    disabled={salvandoAlerta}
                  >
                    {salvandoAlerta ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                        ></span>
                        Salvando...
                      </>
                    ) : (
                      <>🔔 Salvar Alertas</>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* ✅ MODAL DE JUSTIFICATIVA ATUALIZADO COM BOTÃO SALVAR */}
        {modalJustIndex !== null && (
          <div
            className="modal d-block"
            tabIndex="-1"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">📝 Editar Justificativa</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeJustModal}
                  ></button>
                </div>
                <div className="modal-body">
                  <textarea
                    className="form-control"
                    rows="10"
                    value={paginatedData[modalJustIndex]?.justificativa || ""}
                    onChange={(e) => handleJustChange(e.target.value)}
                    placeholder="Digite sua observação aqui..."
                    disabled={salvandoObservacao}
                  />
                  {salvandoObservacao && (
                    <div className="text-center mt-2">
                      <div
                        className="spinner-border spinner-border-sm text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Salvando...</span>
                      </div>
                      <small className="text-muted ms-2">
                        Salvando observação...
                      </small>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeJustModal}
                    disabled={salvandoObservacao}
                  >
                    Fechar
                  </button>
                  <button
                    type="button"
                    className={`btn btn-primary ${
                      salvandoObservacao ? "disabled" : ""
                    }`}
                    onClick={salvarObservacao}
                    disabled={salvandoObservacao}
                  >
                    {salvandoObservacao ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                        ></span>
                        Salvando...
                      </>
                    ) : (
                      <>💾 Salvar</>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* ✅ MODAL DE LISTAGEM DE MOVIMENTOS */}
        {modalListVisible !== null && (
          <div
            className="modal d-block"
            tabIndex="-1"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <div
              className="modal-dialog modal-xl"
              style={{
                maxWidth: "min(1400px, 95vw)",
                // Teto = viewport menos a margem do próprio .modal-dialog.
                maxHeight: "calc(100% - 3.5rem)",
                // Sem flex o teto acima não vale nada: o .modal-dialog é
                // display:block no Bootstrap, então o .modal-content não era
                // item flex, não encolhia e transbordava — e quem rolava
                // passava a ser o .modal, que cobre a viewport inteira e por
                // isso parece a barra "da tela".
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div
                className="modal-content"
                // minHeight: 0 porque item flex tem min-height: auto, que o
                // impede de ficar menor que o conteúdo.
                style={{ minHeight: 0, overflow: "hidden" }}
              >
                <div className="modal-header">
                  <h5 className="modal-title">
                    📋 Listagem de Movimentos -{" "}
                    {paginatedData[modalListVisible]?.natureza}
                    <small className="text-muted ms-2">
                      ({paginatedData[modalListVisible]?.codigo})
                    </small>
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeListModal}
                  ></button>
                </div>
                {/* Coluna flex: a busca fica fixa e só a tabela rola. */}
                <div
                  className="modal-body d-flex flex-column"
                  style={{ minHeight: 0 }}
                >
                  {loadingMovimentos ? (
                    <div className="text-center py-4">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">
                          Carregando movimentos...
                        </span>
                      </div>
                      <p className="mt-2">Carregando movimentos...</p>
                    </div>
                  ) : movimentosData.length > 0 ? (
                    <>
                      {/* ✅ BUSCA E FILTROS (estilo em src/index.css) */}
                      {/* flex-shrink-0: quem cede altura é a tabela */}
                      <div className="mb-3 flex-shrink-0">
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <div className="busca-movimentos">
                            <span className="busca-movimentos__icone">🔍</span>
                            <input
                              type="text"
                              className="busca-movimentos__campo"
                              placeholder="Buscar em qualquer coluna — fornecedor, valor, data, tipo..."
                              value={buscaMovimentos}
                              onChange={(e) =>
                                setBuscaMovimentos(e.target.value)
                              }
                            />
                            {buscaMovimentos && (
                              <button
                                type="button"
                                className="busca-movimentos__limpar"
                                onClick={() => setBuscaMovimentos("")}
                                title="Limpar busca"
                              >
                                ✕
                              </button>
                            )}
                          </div>

                          {movimentosEstaoFiltrados && (
                            <span className="busca-movimentos-resultado">
                              <strong>{movimentosFiltrados.length}</strong> de{" "}
                              {movimentosData.length} registro(s)
                            </span>
                          )}

                          {/* âncora do menu, encostada na direita: o painel é
                              absolute em relação a ela e abre alinhado por ela */}
                          {FILTROS_HABILITADOS && (
                          <div className="filtros-movimentos-ancora">
                            <button
                              type="button"
                              className={`btn btn-sm ${
                                filtrosAbertos || temFiltroAvancado
                                  ? "btn-primary"
                                  : "btn-outline-secondary"
                              }`}
                              onClick={() =>
                                setFiltrosAbertos((aberto) => !aberto)
                              }
                              title="Fornecedor, período, faixa de valor e mais"
                            >
                              ⚙️ Filtros
                              {temFiltroAvancado && (
                                <span className="badge bg-light text-dark ms-2">
                                  {filtrosAtivos.length}
                                </span>
                              )}
                              <span className="ms-2">
                                {filtrosAbertos ? "▲" : "▼"}
                              </span>
                            </button>

                            {filtrosAbertos && (
                              <>
                                {/* fundo invisível: clicar fora fecha o menu,
                                    sem precisar de listener no document */}
                                <div
                                  className="filtros-movimentos__fundo"
                                  onClick={() => setFiltrosAbertos(false)}
                                />
                                <div className="filtros-movimentos">
                                  <div className="filtros-movimentos__campo">
                                    <span className="filtros-movimentos__rotulo">
                                      Fornecedor
                                    </span>
                                    <select
                                      className="form-select form-select-sm"
                                      value={filtrosMovimentos.fornecedor}
                                      onChange={(e) =>
                                        alterarFiltroMovimentos(
                                          "fornecedor",
                                          e.target.value
                                        )
                                      }
                                    >
                                      <option value="">
                                        Todos ({movimentosData.length})
                                      </option>
                                      {fornecedoresMovimentos.map(
                                        (fornecedor) => (
                                          <option
                                            key={fornecedor.nome}
                                            value={fornecedor.nome}
                                          >
                                            {fornecedor.nome} (
                                            {fornecedor.quantidade})
                                          </option>
                                        )
                                      )}
                                    </select>
                                  </div>

                                  <div className="filtros-movimentos__campo">
                                    <span className="filtros-movimentos__rotulo">
                                      Período
                                    </span>
                                    <div className="filtros-movimentos__intervalo">
                                      <input
                                        type="date"
                                        className="form-control form-control-sm"
                                        value={filtrosMovimentos.dataDe}
                                        onChange={(e) =>
                                          alterarFiltroMovimentos(
                                            "dataDe",
                                            e.target.value
                                          )
                                        }
                                        title="Data inicial"
                                      />
                                      <span className="filtros-movimentos__ate">
                                        →
                                      </span>
                                      <input
                                        type="date"
                                        className="form-control form-control-sm"
                                        value={filtrosMovimentos.dataAte}
                                        onChange={(e) =>
                                          alterarFiltroMovimentos(
                                            "dataAte",
                                            e.target.value
                                          )
                                        }
                                        title="Data final"
                                      />
                                    </div>
                                  </div>

                                  <div className="filtros-movimentos__campo">
                                    <span className="filtros-movimentos__rotulo">
                                      Valor líquido
                                    </span>
                                    <div className="filtros-movimentos__intervalo">
                                      <input
                                        type="text"
                                        inputMode="decimal"
                                        className="form-control form-control-sm"
                                        placeholder="Mín."
                                        value={filtrosMovimentos.valorMin}
                                        onChange={(e) =>
                                          alterarFiltroMovimentos(
                                            "valorMin",
                                            e.target.value
                                          )
                                        }
                                      />
                                      <span className="filtros-movimentos__ate">
                                        →
                                      </span>
                                      <input
                                        type="text"
                                        inputMode="decimal"
                                        className="form-control form-control-sm"
                                        placeholder="Máx."
                                        value={filtrosMovimentos.valorMax}
                                        onChange={(e) =>
                                          alterarFiltroMovimentos(
                                            "valorMax",
                                            e.target.value
                                          )
                                        }
                                      />
                                    </div>
                                  </div>

                                  <div className="filtros-movimentos__divisoria" />

                                  <div className="form-check form-switch filtros-movimentos__switch">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      role="switch"
                                      id="filtro-mov-com-valor"
                                      checked={
                                        filtrosMovimentos.somenteComValor
                                      }
                                      onChange={(e) =>
                                        alterarFiltroMovimentos(
                                          "somenteComValor",
                                          e.target.checked
                                        )
                                      }
                                    />
                                    <label
                                      className="form-check-label"
                                      htmlFor="filtro-mov-com-valor"
                                    >
                                      Com valor líquido
                                    </label>
                                  </div>
                                  <div className="form-check form-switch filtros-movimentos__switch">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      role="switch"
                                      id="filtro-mov-com-empenhado"
                                      checked={
                                        filtrosMovimentos.somenteComEmpenhado
                                      }
                                      onChange={(e) =>
                                        alterarFiltroMovimentos(
                                          "somenteComEmpenhado",
                                          e.target.checked
                                        )
                                      }
                                    />
                                    <label
                                      className="form-check-label"
                                      htmlFor="filtro-mov-com-empenhado"
                                    >
                                      Com empenhado
                                    </label>
                                  </div>

                                  <div className="filtros-movimentos__divisoria" />

                                  <div className="filtros-movimentos__acoes">
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-link p-0 text-decoration-none"
                                      onClick={limparFiltrosMovimentos}
                                      disabled={!temFiltroAvancado}
                                    >
                                      🧹 Limpar
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-secondary"
                                      onClick={() => setFiltrosAbertos(false)}
                                    >
                                      Fechar
                                    </button>
                                  </div>
                                </div>
                              </>
                            )}
                          </div>
                          )}
                        </div>

                        {FILTROS_HABILITADOS && temFiltroAvancado && (
                          <div className="filtros-movimentos-chips mt-2">
                            {filtrosAtivos.map(({ chave, rotulo }) => (
                              <button
                                key={chave}
                                type="button"
                                className="filtro-chip"
                                onClick={() => removerFiltroMovimentos(chave)}
                                title="Remover este filtro"
                              >
                                {rotulo}
                                <span className="filtro-chip__x">✕</span>
                              </button>
                            ))}
                            <button
                              type="button"
                              className="filtro-chip filtro-chip--limpar"
                              onClick={limparFiltrosMovimentos}
                              title="Remover todos os filtros"
                            >
                              🧹 Limpar filtros
                            </button>
                          </div>
                        )}
                      </div>
                      <div
                        className="table-responsive"
                        style={{ minHeight: 0, overflowY: "auto" }}
                      >
                      <table className="table table-bordered table-striped table-hover align-middle">
                        <thead className="table-dark sticky-top">
                          <tr>
                            {renderThMovimento("idMov", "ID Mov", "80px")}
                            {renderThMovimento("data", "Data", "100px")}
                            {renderThMovimento("fornecedor", "Fornecedor", "200px")}
                            {renderThMovimento("valor", "Valor Líquido", "120px")}
                            {renderThMovimento("empenhado", "Empenhado", "120px")}
                            {renderThMovimento("status", "Financeiro", "80px")}
                            {renderThMovimento("tipo", "Tipo", "80px")}
                            <th style={{ minWidth: "120px" }}>Ações</th>
                          </tr>
                        </thead>
                        <tbody>
                          {movimentosFiltrados.length === 0 && (
                            <tr>
                              <td
                                colSpan={8}
                                className="text-center text-muted py-3"
                              >
                                Nenhum movimento corresponde aos filtros
                                aplicados.
                              </td>
                            </tr>
                          )}
                          {movimentosFiltrados.map((movimento, index) => (
                            <tr key={`${movimento.idMov}-${index}`}>
                              <td>
                                <span className="badge bg-secondary">
                                  {movimento.idMov}
                                </span>
                              </td>
                              <td>{movimento.data}</td>
                              <td>
                                <div>
                                  <strong>{movimento.fornecedor}</strong>
                                  {movimento.origem !== "N/A" && (
                                    <>
                                      <br />
                                      <small className="text-muted">
                                        Origem: {movimento.origem}
                                      </small>
                                    </>
                                  )}
                                  {movimento.destino !== "N/A" && (
                                    <>
                                      <br />
                                      <small className="text-muted">
                                        Destino: {movimento.destino}
                                      </small>
                                    </>
                                  )}
                                </div>
                              </td>
                              <td>
                                <span
                                  className={`badge ${
                                    movimento.valor >= 0
                                      ? "bg-success"
                                      : "bg-danger"
                                  }`}
                                >
                                  {movimento.valor.toLocaleString("pt-BR", {
                                    style: "currency",
                                    currency: "BRL",
                                  })}
                                </span>
                              </td>
                              <td>
                                <span className="badge bg-warning text-dark">
                                  {movimento.empenhado.toLocaleString("pt-BR", {
                                    style: "currency",
                                    currency: "BRL",
                                  })}
                                </span>
                              </td>
                              <td>
                                <span
                                  className={`badge ${
                                    movimento.status === "Baixado"
                                      ? "bg-success"
                                      : movimento.status === "Em Aberto"
                                      ? "bg-warning text-dark"
                                      : movimento.status === "Cancelado"
                                      ? "bg-danger"
                                      : "bg-secondary"
                                  }`}
                                >
                                  {movimento.status}
                                </span>
                              </td>
                              <td>
                                <small>{movimento.tipo}</small>
                              </td>
                              <td>
                                <div className="d-grid gap-1">
                                  <button
                                    style={{ whiteSpace: "nowrap" }}
                                    className={`btn btn-sm ${
                                      movimento.valor > 0
                                        ? "btn-outline-primary"
                                        : "btn-outline-secondary disabled"
                                    }`}
                                    onClick={() =>
                                      movimento.valor > 0 &&
                                      openItensModal(movimento)
                                    }
                                    disabled={movimento.valor <= 0}
                                    title={
                                      movimento.valor > 0
                                        ? "Ver detalhes dos itens"
                                        : "Itens indisponíveis para movimentos sem valor"
                                    }
                                  >
                                    📦 Itens
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-4">
                      <div className="alert alert-info">
                        <i className="bi bi-info-circle"></i>
                        <strong>Nenhum movimento encontrado</strong>
                        <p className="mb-0">
                          Não foram encontrados movimentos para esta combinação
                          de centro de custo e natureza orçamentária.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <div className="me-auto">
                    <small className="text-muted d-block mb-2">
                      Total de registros:{" "}
                      <strong>{movimentosFiltrados.length}</strong>
                      {movimentosEstaoFiltrados && (
                        <span className="text-warning fw-bold ms-1">
                          (filtrado de {movimentosData.length})
                        </span>
                      )}
                    </small>
                    {movimentosFiltrados.length > 0 && (
                      <div className="totais-movimentos">
                        <span
                          className={classeTotalMovimento(
                            "realizado",
                            totaisMovimentos.realizado
                          )}
                        >
                          Total Realizado
                          <strong>
                            {moedaParaTexto(totaisMovimentos.realizado)}
                          </strong>
                        </span>
                        <span
                          className={classeTotalMovimento(
                            "empenhado",
                            totaisMovimentos.empenhado
                          )}
                        >
                          Total Empenhado
                          <strong>
                            {moedaParaTexto(totaisMovimentos.empenhado)}
                          </strong>
                        </span>
                        <span
                          className={classeTotalMovimento(
                            "total",
                            totaisMovimentos.total
                          )}
                        >
                          Total Realizado + Total Empenhado
                          <strong>
                            {moedaParaTexto(totaisMovimentos.total)}
                          </strong>
                        </span>
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeListModal}
                  >
                    Fechar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* ✅ MODAL DE ITENS DO MOVIMENTO */}
        {modalItensVisible !== null && (
          <div
            className="modal d-block"
            tabIndex="-1"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <div className="modal-dialog modal-xl">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    📦 Itens do Movimento - {modalItensVisible.destino} -{" "}
                    {modalItensVisible.fornecedor}
                    <small className="text-muted ms-2">
                      (ID: {modalItensVisible.idMov} | Data:{" "}
                      {modalItensVisible.data})
                    </small>
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeItensModal}
                  ></button>
                </div>
                <div className="modal-body">
                  {loadingItens ? (
                    <div className="text-center py-4">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">
                          Carregando itens...
                        </span>
                      </div>
                      <p className="mt-2">Carregando itens do movimento...</p>
                    </div>
                  ) : itensMovimento.length > 0 ? (
                    <div
                      className="table-responsive"
                      style={{ maxHeight: "60vh", overflowY: "auto" }}
                    >
                      <table className="table table-bordered table-striped table-hover">
                        <thead className="table-dark sticky-top">
                          <tr>
                            <th style={{ minWidth: "100px" }}>Código</th>
                            <th style={{ minWidth: "250px" }}>Descrição</th>
                            <th style={{ minWidth: "80px" }}>Qtd</th>
                            <th style={{ minWidth: "60px" }}>Unidade</th>
                            <th style={{ minWidth: "120px" }}>Valor Unit.</th>
                            <th style={{ minWidth: "120px" }}>Valor Total</th>
                            <th style={{ minWidth: "100px" }}>NF</th>
                          </tr>
                        </thead>
                        <tbody>
                          {itensMovimento.map((item, index) => (
                            <tr key={`${item.idItem}-${index}`}>
                              <td>
                                <span className="badge bg-secondary">
                                  {item.codProduto}
                                </span>
                                <br />
                                <small className="text-muted">
                                  {item.idItem}
                                </small>
                              </td>
                              <td>
                                <div>
                                  <strong>{item.descricao}</strong>
                                  {item.observacao && (
                                    <>
                                      <br />
                                      <small className="text-muted">
                                        📝 {item.observacao}
                                      </small>
                                    </>
                                  )}
                                </div>
                              </td>
                              <td className="text-center">
                                <span className="badge bg-info text-dark">
                                  {item.quantidade.toLocaleString("pt-BR", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 3,
                                  })}
                                </span>
                              </td>
                              <td className="text-center">
                                <small>{item.unidade}</small>
                              </td>
                              <td className="text-end">
                                <span className="badge bg-warning text-dark">
                                  {item.valorUnitario.toLocaleString("pt-BR", {
                                    style: "currency",
                                    currency: "BRL",
                                  })}
                                </span>
                              </td>
                              <td className="text-end">
                                <span
                                  className={`badge ${
                                    item.valorTotal >= 0
                                      ? "bg-success"
                                      : "bg-danger"
                                  }`}
                                >
                                  {item.valorTotal.toLocaleString("pt-BR", {
                                    style: "currency",
                                    currency: "BRL",
                                  })}
                                </span>
                              </td>
                              <td>
                                <small>
                                  {item.numeroNF !== "N/A" && (
                                    <>
                                      NF: {item.numeroNF}
                                      {item.serieNF !== "N/A" && <br />}
                                      {item.serieNF !== "N/A" && (
                                        <>Série: {item.serieNF}</>
                                      )}
                                    </>
                                  )}
                                </small>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <div className="alert alert-warning">
                        <i className="bi bi-exclamation-triangle"></i>
                        <strong>Nenhum item encontrado</strong>
                        <p className="mb-0">
                          Não foram encontrados itens para este movimento.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <div className="me-auto">
                    <small className="text-muted">
                      Total de itens: <strong>{itensMovimento.length}</strong>
                      {itensMovimento.length > 0 && (
                        <>
                          <span className="ms-3">
                            Qtd total:{" "}
                            <strong>
                              {itensMovimento
                                .reduce((acc, item) => acc + item.quantidade, 0)
                                .toLocaleString("pt-BR", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 3,
                                })}
                            </strong>
                          </span>
                          <span className="ms-3">
                            Valor total:{" "}
                            <strong>
                              {itensMovimento
                                .reduce((acc, item) => acc + item.valorTotal, 0)
                                .toLocaleString("pt-BR", {
                                  style: "currency",
                                  currency: "BRL",
                                })}
                            </strong>
                          </span>
                        </>
                      )}
                    </small>
                  </div>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeItensModal}
                  >
                    Fechar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* ✅ MODAL DE HISTÓRICO DE VERSÕES */}
        {modalVersaoVisible !== null && (
          <div
            className="modal d-block"
            tabIndex="-1"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <div className="modal-dialog modal-xl">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    🕓 Histórico de Versões - {modalVersaoVisible.natureza}
                    <small className="text-muted ms-2">
                      (ID Orçamento: {modalVersaoVisible.idOrcto})
                    </small>
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeVersaoModal}
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="row">
                    {/* Lista de Versões */}
                    <div className={versaoDetalhada ? "col-md-6" : "col-12"}>
                      {loadingVersoes ? (
                        <div className="text-center py-4">
                          <div
                            className="spinner-border text-primary"
                            role="status"
                          >
                            <span className="visually-hidden">
                              Carregando versões...
                            </span>
                          </div>
                          <p className="mt-2">
                            Carregando histórico de versões...
                          </p>
                        </div>
                      ) : versoesData.length > 0 ? (
                        <div>
                          <h6 className="mb-3">📋 Versões Disponíveis</h6>
                          <div
                            className="list-group"
                            style={{ maxHeight: "60vh", overflowY: "auto" }}
                          >
                            {versoesData.map((versao, index) => (
                              <div
                                key={`${versao.id}-${index}`}
                                className={`list-group-item list-group-item-action ${
                                  versaoDetalhada &&
                                  versaoDetalhada.id === versao.id
                                    ? "active"
                                    : ""
                                }`}
                                onClick={() => verDetalhesVersao(versao)}
                                style={{ cursor: "pointer" }}
                              >
                                <div className="d-flex w-100 justify-content-between">
                                  <h6 className="mb-1">
                                    Versão {versao.versao}
                                    {index === 0 && (
                                      <span className="badge bg-success ms-2">
                                        Atual
                                      </span>
                                    )}
                                  </h6>
                                  <small>{versao.data}</small>
                                </div>
                                <p className="mb-1">
                                  <strong>Usuário:</strong> {versao.nome}
                                </p>
                                <small className="text-muted">
                                  Clique para ver detalhes
                                </small>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-4">
                          <div className="alert alert-info">
                            <i className="bi bi-info-circle"></i>
                            <strong>Nenhuma versão encontrada</strong>
                            <p className="mb-0">
                              Não há histórico de versões para este orçamento.
                            </p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Detalhes da Versão */}
                    {versaoDetalhada && (
                      <div className="col-md-6">
                        <div className="card">
                          <div className="card-header bg-primary text-white">
                            <h6 className="mb-0">
                              {versaoDetalhada.versao1
                                ? "📊 Comparação de Versões"
                                : "📄 Detalhes da Versão"}
                            </h6>
                          </div>
                          <div className="card-body">
                            {versaoDetalhada.versao1 ? (
                              // Modo comparação
                              <div>
                                <div className="row">
                                  <div className="col-6">
                                    <h6 className="text-center text-primary">
                                      Versão {versaoDetalhada.versao1.versao}
                                    </h6>
                                  </div>
                                  <div className="col-6">
                                    <h6 className="text-center text-success">
                                      Versão {versaoDetalhada.versao2.versao}
                                    </h6>
                                  </div>
                                </div>

                                <table className="table table-sm table-bordered mt-3">
                                  <tbody>
                                    <tr
                                      className={
                                        versaoDetalhada.diferencas.usuario
                                          ? "table-warning"
                                          : ""
                                      }
                                    >
                                      <td>
                                        <strong>Usuário</strong>
                                      </td>
                                      <td>{versaoDetalhada.versao1.nome}</td>
                                      <td>{versaoDetalhada.versao2.nome}</td>
                                    </tr>
                                    <tr
                                      className={
                                        versaoDetalhada.diferencas.data
                                          ? "table-warning"
                                          : ""
                                      }
                                    >
                                      <td>
                                        <strong>Data</strong>
                                      </td>
                                      <td>{versaoDetalhada.versao1.data}</td>
                                      <td>{versaoDetalhada.versao2.data}</td>
                                    </tr>
                                  </tbody>
                                </table>

                                {versaoDetalhada.diferencas.valor && (
                                  <div className="alert alert-info mt-3">
                                    <strong>
                                      ⚠️ Alterações detectadas no conteúdo SQL
                                    </strong>
                                  </div>
                                )}
                              </div>
                            ) : (
                              // Modo detalhes
                              <div>
                                <dl className="row">
                                  <dt className="col-sm-4">Versão:</dt>
                                  <dd className="col-sm-8">
                                    <span className="badge bg-primary">
                                      {versaoDetalhada.versao}
                                    </span>
                                  </dd>

                                  <dt className="col-sm-4">Usuário:</dt>
                                  <dd className="col-sm-8">
                                    {versaoDetalhada.nome}
                                  </dd>

                                  <dt className="col-sm-4">Data/Hora:</dt>
                                  <dd className="col-sm-8">
                                    {versaoDetalhada.data}
                                  </dd>

                                  <dt className="col-sm-4">ID Orçamento:</dt>
                                  <dd className="col-sm-8">
                                    <code>{versaoDetalhada.idOrcto}</code>
                                  </dd>
                                </dl>

                                {versaoDetalhada.valor && (
                                  <div className="mt-3">
                                    <h6>📝 Valor:</h6>
                                    <div
                                      className="bg-light p-3 rounded"
                                      style={{
                                        maxHeight: "300px",
                                        overflowY: "auto",
                                      }}
                                    >
                                      <pre className="mb-0">
                                        <code>{versaoDetalhada.valor}</code>
                                      </pre>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="modal-footer">
                  <div className="me-auto">
                    <small className="text-muted">
                      Total de versões: <strong>{versoesData.length}</strong>
                    </small>
                  </div>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeVersaoModal}
                  >
                    Fechar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* ✅ MODAL DE DADOS DO ANO ANTERIOR */}
        {modalAnoAnteriorVisible !== null && (
          <div
            className="modal d-block"
            tabIndex="-1"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header bg-info text-white">
                  <h5 className="modal-title">
                    📅 Dados do Ano Anterior - {dadosAnoAnterior?.natureza}
                    <small className="ms-2">
                      ({dadosAnoAnterior?.codigo} - Ano {dadosAnoAnterior?.ano})
                    </small>
                  </h5>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={fecharModalAnoAnterior}
                  ></button>
                </div>
                <div className="modal-body">
                  {loadingAnoAnterior ? (
                    <div className="text-center py-4">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">
                          Carregando dados do ano anterior...
                        </span>
                      </div>
                      <p className="mt-2">
                        Carregando dados do ano anterior...
                      </p>
                    </div>
                  ) : dadosAnoAnterior ? (
                    <div>
                      {/* Informações básicas */}
                      <div className="card mb-3">
                        <div className="card-header bg-light">
                          <h6 className="mb-0">ℹ️ Informações Básicas</h6>
                        </div>
                        <div className="card-body">
                          <div className="row">
                            <div className="col-md-6">
                              <strong>Natureza:</strong>{" "}
                              {dadosAnoAnterior.natureza}
                            </div>
                            <div className="col-md-6">
                              <strong>Código:</strong> {dadosAnoAnterior.codigo}
                            </div>
                            <div className="col-md-12 mt-2">
                              <strong>Centro de Custo:</strong>{" "}
                              {dadosAnoAnterior.centroCusto}
                            </div>
                          </div>
                        </div>
                      </div>

                      {dadosAnoAnterior.semDados ? (
                        <div className="alert alert-warning text-center">
                          <i className="bi bi-exclamation-triangle"></i>
                          <h6>
                            Nenhum dado encontrado para o ano{" "}
                            {dadosAnoAnterior.ano}
                          </h6>
                          <p className="mb-0">
                            Não foram encontrados registros para esta natureza
                            orçamentária no ano anterior.
                          </p>
                        </div>
                      ) : (
                        <div>
                          {/* Cards com os valores principais */}
                          <div className="row g-3 mb-4">
                            {/* Orçado */}
                            <div className="col-md-4">
                              <div className="card border-primary">
                                <div className="card-body text-center">
                                  <h6 className="card-title text-primary">
                                    💰 Orçado
                                  </h6>
                                  <h5 className="text-primary">
                                    {dadosAnoAnterior.orcado.toLocaleString(
                                      "pt-BR",
                                      {
                                        style: "currency",
                                        currency: "BRL",
                                      }
                                    )}
                                  </h5>
                                </div>
                              </div>
                            </div>

                            {/* Realizado */}
                            <div className="col-md-4">
                              <div className="card border-danger">
                                <div className="card-body text-center">
                                  <h6 className="card-title text-danger">
                                    📊 Realizado
                                  </h6>
                                  <h5 className="text-danger">
                                    {dadosAnoAnterior.realizado.toLocaleString(
                                      "pt-BR",
                                      {
                                        style: "currency",
                                        currency: "BRL",
                                      }
                                    )}
                                  </h5>
                                </div>
                              </div>
                            </div>

                            {/* Economia 
                            <div className="col-md-3">
                              <div className="card border-success">
                                <div className="card-body text-center">
                                  <h6 className="card-title text-success">
                                    💎 Economia
                                  </h6>
                                  <h5 className="text-success">
                                    {dadosAnoAnterior.economia.toLocaleString(
                                      "pt-BR",
                                      {
                                        style: "currency",
                                        currency: "BRL",
                                      }
                                    )}
                                  </h5>
                                </div>
                              </div>
                            </div>*/}

                            {/* Eficiente */}
                            <div className="col-md-4">
                              <div className="card border-warning">
                                <div className="card-body text-center">
                                  <h6 className="card-title text-warning">
                                    ⚡ Executado
                                  </h6>
                                  <h5 className="text-warning">
                                    {dadosAnoAnterior.eficiente} %
                                  </h5>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Informações calculadas */}
                          <div className="row g-3">
                            {/* Saldo */}
                            <div className="col-md-4">
                              <div
                                className={`card ${
                                  dadosAnoAnterior.saldo >= 0
                                    ? "border-success"
                                    : "border-danger"
                                }`}
                              >
                                <div className="card-body text-center">
                                  <h6 className="card-title">💳 Saldo</h6>
                                  <h5
                                    className={
                                      dadosAnoAnterior.saldo >= 0
                                        ? "text-success"
                                        : "text-danger"
                                    }
                                  >
                                    {dadosAnoAnterior.saldo.toLocaleString(
                                      "pt-BR",
                                      {
                                        style: "currency",
                                        currency: "BRL",
                                      }
                                    )}
                                  </h5>
                                </div>
                              </div>
                            </div>

                            {/* Consumo */}
                            <div className="col-md-8">
                              <div className="card">
                                <div className="card-body">
                                  <h6 className="card-title">
                                    📈 Consumo do Orçamento
                                  </h6>
                                  <div
                                    className="progress"
                                    style={{ height: "2rem" }}
                                  >
                                    <div
                                      className={`progress-bar ${
                                        dadosAnoAnterior.consumoPercent > 80
                                          ? "bg-danger"
                                          : dadosAnoAnterior.consumoPercent > 50
                                          ? "bg-warning"
                                          : "bg-success"
                                      }`}
                                      role="progressbar"
                                      style={{
                                        width: `${Math.min(
                                          100,
                                          dadosAnoAnterior.consumoPercent
                                        )}%`,
                                      }}
                                      aria-valuenow={
                                        dadosAnoAnterior.consumoPercent
                                      }
                                      aria-valuemin="0"
                                      aria-valuemax="100"
                                    >
                                      <strong>
                                        {dadosAnoAnterior.consumoPercent}%
                                      </strong>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Comparação com ano atual */}
                          {paginatedData[modalAnoAnteriorVisible] && (
                            <div className="card mt-4">
                              <div className="card-header bg-secondary text-white">
                                <h6 className="mb-0">
                                  🔄 Comparação com Ano Atual
                                </h6>
                              </div>
                              <div className="card-body">
                                <div className="table-responsive">
                                  <table className="table table-bordered table-sm">
                                    <thead className="table-light">
                                      <tr>
                                        <th>Métrica</th>
                                        <th>
                                          Ano Anterior ({dadosAnoAnterior.ano})
                                        </th>
                                        <th>
                                          Ano Atual (
                                          {localStorage.getItem("selectedYear")}
                                          )
                                        </th>
                                        <th>Variação</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      <tr>
                                        <td>
                                          <strong>Orçado</strong>
                                        </td>
                                        <td>
                                          {dadosAnoAnterior.orcado.toLocaleString(
                                            "pt-BR",
                                            {
                                              style: "currency",
                                              currency: "BRL",
                                            }
                                          )}
                                        </td>
                                        <td>
                                          {paginatedData[
                                            modalAnoAnteriorVisible
                                          ].orcado.toLocaleString("pt-BR", {
                                            style: "currency",
                                            currency: "BRL",
                                          })}
                                        </td>
                                        <td>
                                          <span
                                            className={`badge ${
                                              paginatedData[
                                                modalAnoAnteriorVisible
                                              ].orcado > dadosAnoAnterior.orcado
                                                ? "bg-danger"
                                                : "bg-success"
                                            }`}
                                          >
                                            {paginatedData[
                                              modalAnoAnteriorVisible
                                            ].orcado > dadosAnoAnterior.orcado
                                              ? "📈"
                                              : "📉"}
                                            {(
                                              ((paginatedData[
                                                modalAnoAnteriorVisible
                                              ].orcado -
                                                dadosAnoAnterior.orcado) /
                                                (dadosAnoAnterior.orcado ||
                                                  1)) *
                                              100
                                            ).toFixed(1)}
                                            %
                                          </span>
                                        </td>
                                      </tr>
                                      <tr>
                                        <td>
                                          <strong>Realizado</strong>
                                        </td>
                                        <td>
                                          {dadosAnoAnterior.realizado.toLocaleString(
                                            "pt-BR",
                                            {
                                              style: "currency",
                                              currency: "BRL",
                                            }
                                          )}
                                        </td>
                                        <td>
                                          {paginatedData[
                                            modalAnoAnteriorVisible
                                          ].realizado.toLocaleString("pt-BR", {
                                            style: "currency",
                                            currency: "BRL",
                                          })}
                                        </td>
                                        <td>
                                          <span
                                            className={`badge ${
                                              paginatedData[
                                                modalAnoAnteriorVisible
                                              ].realizado >
                                              dadosAnoAnterior.realizado
                                                ? "bg-danger"
                                                : "bg-success"
                                            }`}
                                          >
                                            {paginatedData[
                                              modalAnoAnteriorVisible
                                            ].realizado >
                                            dadosAnoAnterior.realizado
                                              ? "📈"
                                              : "📉"}
                                            {(
                                              ((paginatedData[
                                                modalAnoAnteriorVisible
                                              ].realizado -
                                                dadosAnoAnterior.realizado) /
                                                (dadosAnoAnterior.realizado ||
                                                  1)) *
                                              100
                                            ).toFixed(1)}
                                            %
                                          </span>
                                        </td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <div className="alert alert-danger">
                        <i className="bi bi-exclamation-triangle"></i>
                        <strong>Erro ao carregar dados</strong>
                        <p className="mb-0">
                          Não foi possível carregar os dados do ano anterior.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <div className="me-auto">
                    <small className="text-muted">
                      {dadosAnoAnterior && !dadosAnoAnterior.semDados && (
                        <>
                          Dados do ano <strong>{dadosAnoAnterior.ano}</strong> |
                          Consumo:{" "}
                          <strong>{dadosAnoAnterior.consumoPercent}%</strong>
                        </>
                      )}
                    </small>
                  </div>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={fecharModalAnoAnterior}
                  >
                    Fechar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ✅ MODAL DE LOADING PARA RELATÓRIOS */}
        {relatorioLoading && (
          <div
            className="modal d-block"
            tabIndex="-1"
            style={{ backgroundColor: "rgba(0,0,0,0.8)", zIndex: 9999 }}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content border-0 shadow-lg">
                <div className="modal-header bg-primary text-white border-0">
                  <h5 className="modal-title d-flex align-items-center">
                    <div
                      className="spinner-grow spinner-grow-sm me-2"
                      role="status"
                    >
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    📊 Gerando Relatório {relatorioNome}
                  </h5>
                  {relatorioResultado && !relatorioResultado.sucesso && (
                    <button
                      type="button"
                      className="btn-close btn-close-white"
                      onClick={fecharModalRelatorio}
                    ></button>
                  )}
                </div>

                <div className="modal-body text-center py-4">
                  {/* Animação de Loading */}
                  <div className="mb-4">
                    <div className="position-relative d-inline-block">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                        style={{ width: "4rem", height: "4rem" }}
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <div
                        className="position-absolute top-50 start-50 translate-middle"
                        style={{ fontSize: "1.5rem" }}
                      >
                        📊
                      </div>
                    </div>
                  </div>

                  {/* Barra de Progresso */}
                  <div className="mb-3">
                    <div className="progress" style={{ height: "8px" }}>
                      <div
                        className={`progress-bar progress-bar-striped progress-bar-animated ${
                          relatorioProgresso === 100
                            ? relatorioResultado?.sucesso
                              ? "bg-success"
                              : "bg-danger"
                            : "bg-primary"
                        }`}
                        role="progressbar"
                        style={{ width: `${relatorioProgresso}%` }}
                        aria-valuenow={relatorioProgresso}
                        aria-valuemin="0"
                        aria-valuemax="100"
                      ></div>
                    </div>
                    <small className="text-muted">
                      {relatorioProgresso.toFixed(0)}% concluído
                    </small>
                  </div>

                  {/* Mensagem Status */}
                  <div className="mb-3">
                    <p className="mb-1 fw-medium text-primary">
                      {relatorioMensagem}
                    </p>
                    {relatorioResultado && (
                      <div
                        className={`alert ${
                          relatorioResultado.sucesso
                            ? "alert-success"
                            : "alert-danger"
                        } mt-3`}
                      >
                        {relatorioResultado.mensagem}
                      </div>
                    )}
                  </div>

                  {/* Dicas Motivacionais */}
                  {!relatorioResultado && (
                    <div className="mt-4 p-3 bg-light rounded">
                      <h6 className="text-muted mb-2">
                        💡 Enquanto você aguarda...
                      </h6>
                      <small className="text-muted">
                        {relatorioTipo === "lancamentos-analitico"
                          ? "O relatório analítico fornece informações detalhadas para análise profunda dos dados orçamentários."
                          : "O relatório sintético oferece uma visão consolidada e resumida dos principais indicadores."}
                      </small>
                    </div>
                  )}

                  {/* Informações Técnicas */}
                  <div className="mt-3">
                    <small className="text-muted d-block">
                      🏢 Filial: {localStorage.getItem("NomeFilial") || "N/A"}
                    </small>
                    <small className="text-muted d-block">
                      📅 Período:{" "}
                      {localStorage.getItem("selectedYear") ||
                        new Date().getFullYear()}
                    </small>
                    <small className="text-muted d-block">
                      👤 Usuário: {localStorage.getItem("username") || "N/A"}
                    </small>
                  </div>
                </div>

                {/* Footer apenas para erros */}
                {relatorioResultado && !relatorioResultado.sucesso && (
                  <div className="modal-footer border-0">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={fecharModalRelatorio}
                    >
                      Fechar
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => {
                        fecharModalRelatorio();
                        // Tentar novamente
                        setTimeout(() => {
                          if (relatorioTipo === "lancamentos-analitico") {
                            iniciarGeracaoRelatorio(
                              "lancamentos-analitico",
                              "Analítico"
                            );
                          } else {
                            iniciarGeracaoRelatorio(
                              "lancamentos-sintetico",
                              "Sintético"
                            );
                          }
                        }, 100);
                      }}
                    >
                      🔄 Tentar Novamente
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}


        
      </div>
    </div>
  );
}
